import React, { useState, useEffect, useRef } from "react";
import {
  ChakraProvider,
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Spinner,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Heading,
  IconButton,
  useToast,
  Input,
  extendTheme,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Select,
  Textarea,
  FormControl,
  FormLabel,
  Divider,
} from "@chakra-ui/react";
import { FiX, FiMaximize2, FiMinimize2 } from "react-icons/fi";
import Draggable from "react-draggable";
import { basename, tableName } from "./components/env.js";
import {
  getSupabase,
  supabasePublic,
  supabaseSchema,
} from "./components/supabaseClient";
import { getAutoSaveSetting } from "./utils/utils";
import { isClinicChartPath } from "./utils/clinicRoutes";
import { useFloatingBubblesHidden } from "./utils/floatingBubbles";
import {
  initializeCreditTracking,
  addRecordedCredits,
} from "./utils/creditTracker";
import { clearOldLocalStorageAccess } from "./utils/passwordProtection";
import { usePageTimeTracking } from "./utils/pageTimeTracker";
import { usePresencePinger } from "./utils/presencePinger";
import { beginActivity, endActivity } from "./utils/activityTracker";
import { jsPDF } from "jspdf";
import AudioRecorder from "./pages/AudioRecorder.js";
import AudioRecorderPage from "./pages/AudioRecorderPage.js";
import RecordingPage from "./pages/RecordingPage.js";
import RecentAudios from "./components/RecentAudios";
import SearchResultsPage from "./components/SearchComponent";
// import "./css/App.css"; not being used
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import NavBar from "./components/navbar/Navbar.js";
import MessagingHost from "./components/messaging/MessagingHost";
import JsonUploader from "./components/JsonUploader";
import FileUploader from "./components/FileUploader.js";
import PdfViewer from "./components/PdfViewer.js";
import AuthCallback from "./components/auth/login/AuthCallback.js";
import AuthUI from "./components/auth/login/AuthUI.js";
import CompleteSignup from "./components/auth/CompleteSignup.js";
import EmailConfirmation from "./components/auth/EmailConfirmation.js";
import PatientVerification from "./components/auth/PatientVerification.js";
import ResetPassword from "./components/auth/ResetPassword.js";
import LandingPage from "./pages/LandingPage.js";
import SignOut from "./components/SignOut.js";
import SettingsPage from "./components/SettingsPage.js";
import Forbidden from "./components/Forbidden";
import ProtectedRoute from "./components/ProtectedRoute";
import ConfirmAdmin from "./components/ConfirmAdmin.js";
import Schedules from "./pages/Schedules.js";
import PatientListPage from "./pages/PatientListPage";
import NoShows from "./components/NoShows.js";
import PatientPage from "./pages/PatientPage.js";
import MedStudentClinicPage from "./pages/medstudent/MedStudentClinicPage";
import AgentsPage from "./pages/medstudent/agents/AgentsPage";
import {
  RedirectMedStudentAwayFromVisit,
  RedirectNonMedStudentAwayFromClinic,
} from "./components/MedStudentRouteGuard";
import AnalyzePage from "./pages/AnalyzePage.js";
import PersistentComponent from "./components/PersistentComponent.js";
import EditableDocumentPage from "./pages/EditableDocumentPage.js";
import AdminPage from "./pages/AdminPage.js";
import AdminDashboard from "./pages/AdminDashboard.js";
import SuperAdmin from "./pages/SuperAdmin.js";
import AIAssistant from "./components/AIAssistant.js";
import ChatBubble from "./components/ChatBubble.js";
import { isPatientRole } from "./config/roles";
import RecordingBubble from "./components/RecordingBubble.js";
import BubbleContainer from "./components/BubbleContainer.js";
import GlobalReportsModalHost from "./components/GlobalReportsModalHost.js";
import TermsOfService from "./pages/TermsOfService.js";
import TermsOfServiceModal from "./components/TermsOfServiceModal.js";
import MonitorPage from "./pages/MonitorPage.js";
import HelpDeskPage from "./pages/HelpDeskPage.js";
import BookMeetingPage from "./pages/BookMeetingPage.js";
import CompliancePage from "./pages/CompliancePage.js";
import HelpDeskModal from "./components/HelpDeskModal.js";
import ImpersonationBanner from "./components/ImpersonationBanner";
import SingleTabSessionGuard from "./components/SingleTabSessionGuard";
import BadNumberConfirmHost from "./components/sms/BadNumberConfirmHost";
import {
  VOSK_SERVICE_ORIGIN,
  VOSK_JS_URL,
  VOSK_MANIFEST_URL,
  voskModelUrl,
  fetchVoskManifest,
} from "./services/vosk/config.js";

const CLIENT_SYSTEM_STT_MODEL_ID = "vosk-model-small-en-us-0.15";
const ENABLE_CLIENT_SYSTEM_STT_PRELOAD = true;

// Global Email Confirmation Handler
// Checks for confirmation tokens in URL hash on ANY page
function GlobalConfirmationHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');

    if (accessToken && type === 'signup') {
      console.log('🔔 [GLOBAL] Email confirmation token detected, redirecting to confirmation page...');
      // Redirect to confirmation page with the hash preserved
      navigate('/auth/confirm' + window.location.hash);
    }
  }, [navigate]);

  return null;
}
// Custom theme with high z-index for toasts to appear above modals
const theme = extendTheme({
  zIndices: {
    toast: 99999,
  },
});

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  // Define a condition for showing the persistent component
  const [showPersistentComponent, setShowPersistentComponent] = useState(false);
  const [stateData, setStateData] = useState({});
  // Persist session state across page refreshes
  const [session, setSession] = useState(() => {
    const saved = localStorage.getItem("audioRecorderSession");
    return saved === "true";
  });
  const [link, setLink] = useState("");
  const [condFunc, setCondFunc] = useState(undefined);

  // Preload client-side system-audio STT assets early so first external recording feels faster.
  useEffect(() => {
    if (!ENABLE_CLIENT_SYSTEM_STT_PRELOAD) return;
    const CLIENT_STT_DISABLED_KEY = "client_system_stt_disabled";
    // Re-enable client STT after previous disabled-state experiments.
    try {
      localStorage.removeItem(CLIENT_STT_DISABLED_KEY);
    } catch (_) {}
    if (sessionStorage.getItem("client_system_stt_preloaded") === "1") return;

    const downsampleTo16kMono = (input, sourceSampleRate) => {
      if (!input || !input.length) return new Float32Array();
      const srcRate = Number(sourceSampleRate) || 16000;
      if (srcRate === 16000) return input;
      const ratio = srcRate / 16000;
      if (!Number.isFinite(ratio) || ratio <= 0) return input;
      const outLength = Math.max(1, Math.floor(input.length / ratio));
      const output = new Float32Array(outLength);
      let outIndex = 0;
      let inIndex = 0;
      while (outIndex < outLength) {
        const nextInIndex = Math.min(input.length, Math.floor((outIndex + 1) * ratio));
        let accum = 0;
        let count = 0;
        while (inIndex < nextInIndex) {
          accum += input[inIndex];
          inIndex += 1;
          count += 1;
        }
        output[outIndex] = count > 0 ? accum / count : 0;
        outIndex += 1;
      }
      return output;
    };

    const createMonoAudioBuffer = (pcmFloat32, sampleRate = 16000) => {
      const data = pcmFloat32 || new Float32Array();
      if (typeof AudioBuffer === "function") {
        const audioBuffer = new AudioBuffer({
          length: data.length,
          numberOfChannels: 1,
          sampleRate,
        });
        audioBuffer.copyToChannel(data, 0);
        return audioBuffer;
      }
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) throw new Error("AudioContext unavailable for Vosk buffer conversion");
      const ac = new AC({ sampleRate });
      const audioBuffer = ac.createBuffer(1, data.length, sampleRate);
      audioBuffer.copyToChannel(data, 0);
      try {
        ac.close();
      } catch (_) {}
      return audioBuffer;
    };

    // Expose getter immediately so popup can call even before warmup finishes.
    // It lazily loads/caches the model on first use.
    if (typeof window !== "undefined" && typeof window.__notesaiClientSystemSttGet !== "function") {
      let __sttPromise = null;
      window.__notesaiClientSystemSttGet = async () => {
        if (__sttPromise) return __sttPromise;
        __sttPromise = (async () => {
          // Resolve the Vosk service URLs from the manifest (notesai-vosk-service CDN).
          // Fall back to the legacy same-origin /vendors/vosk.js + /<model>.tar.gz paths
          // so the SPA still works if REACT_APP_VOSK_SERVICE_ORIGIN is misconfigured and
          // someone happens to ship a local public/vendors/vosk.js + model archive.
          let scriptCandidates = [];
          let modelCandidates = [];
          try {
            const manifest = await fetchVoskManifest();
            const entry =
              (Array.isArray(manifest?.models) &&
                manifest.models.find((m) => m?.id === CLIENT_SYSTEM_STT_MODEL_ID)) ||
              (Array.isArray(manifest?.models) && manifest.models[0]);
            if (entry) {
              const resolvedModelUrl = voskModelUrl(entry);
              scriptCandidates = [VOSK_JS_URL];
              modelCandidates = [resolvedModelUrl];
              if (typeof window !== "undefined") {
                window.__notesaiVoskServiceConfig = {
                  origin: VOSK_SERVICE_ORIGIN,
                  manifestUrl: VOSK_MANIFEST_URL,
                  scriptUrl: VOSK_JS_URL,
                  modelUrl: resolvedModelUrl,
                  entryId: entry.id,
                };
              }
              console.log(
                "🎤 [CLIENT-STT] Resolved Vosk service URLs:",
                VOSK_JS_URL,
                resolvedModelUrl,
              );
            }
          } catch (manifestErr) {
            console.warn(
              "🎤 [CLIENT-STT] Vosk manifest unreachable; using same-origin fallback candidates:",
              manifestErr?.message || manifestErr,
            );
          }
          scriptCandidates = scriptCandidates.concat([
            "/vendors/vosk.js",
            "/NotesAI/vendors/vosk.js",
          ]);

          const ensureVoskCreateModel = async () => {
            if (typeof window.createModel === "function") return window.createModel;
            if (typeof window.Vosk?.createModel === "function") {
              return window.Vosk.createModel.bind(window.Vosk);
            }
            const loadScriptViaSrc = async (scriptSrc) => {
              const resp = await fetch(scriptSrc, { cache: "default" });
              if (!resp.ok) throw new Error(`HTTP ${resp.status} for ${scriptSrc}`);
              const head = (await resp.text()).trimStart().slice(0, 64);
              if (!head || head.startsWith("<")) {
                throw new Error(`Invalid JavaScript payload for ${scriptSrc}`);
              }
              const absoluteSrc = new URL(scriptSrc, window.location.href).href;
              await new Promise((resolve, reject) => {
                const scripts = document.querySelectorAll("script[data-vosk-src]");
                for (const s of scripts) {
                  if (s.getAttribute("data-vosk-src") === absoluteSrc) {
                    resolve();
                    return;
                  }
                }
                const script = document.createElement("script");
                script.async = true;
                script.src = absoluteSrc;
                script.dataset.voskSrc = absoluteSrc;
                script.onload = () => resolve();
                script.onerror = () =>
                  reject(new Error(`Failed to load script ${absoluteSrc}`));
                document.head.appendChild(script);
              });
            };
            let scriptErr = null;
            for (const scriptSrc of scriptCandidates) {
              try {
                const absoluteSrc = new URL(scriptSrc, window.location.href).href;
                let existing = false;
                for (const s of document.querySelectorAll("script[data-vosk-src]")) {
                  if (s.getAttribute("data-vosk-src") === absoluteSrc) {
                    existing = true;
                    break;
                  }
                }
                if (!existing) {
                  await loadScriptViaSrc(scriptSrc);
                }
                if (typeof window.createModel === "function") return window.createModel;
                if (typeof window.Vosk?.createModel === "function") {
                  return window.Vosk.createModel.bind(window.Vosk);
                }
              } catch (err) {
                scriptErr = err;
              }
            }
            throw scriptErr || new Error("Vosk browser loader missing at /vendors/vosk.js");
          };

          const createModel = await ensureVoskCreateModel();
          const MODEL_LOAD_TIMEOUT_MS = 180000;
          // Try /NotesAI/... first when the app is hosted under that path (root URLs often return index.html).
          modelCandidates = modelCandidates.concat([
            "/NotesAI/vosk-model-small-en-us-0.15-flat.tar.gz",
            "/NotesAI/vosk-model-small-en-us-0.15.tar.gz",
            "/NotesAI/vosk-model-small-en-us-0.15.zip",
            "/NotesAI/model.tar.gz",
            "/NotesAI/model.zip",
            "/vosk-model-small-en-us-0.15-flat.tar.gz",
            "/vosk-model-small-en-us-0.15.tar.gz",
            "/vosk-model-small-en-us-0.15.zip",
            `/${CLIENT_SYSTEM_STT_MODEL_ID}.zip`,
            "/v0.3.43.tar.gz",
            `/${CLIENT_SYSTEM_STT_MODEL_ID}.tar.gz`,
            "/model.tar.gz",
            "/model.zip",
          ]);
          const loadModelWithTimeout = async (createModelFn, modelPath) =>
            Promise.race([
              createModelFn(modelPath),
              new Promise((_, reject) =>
                setTimeout(
                  () =>
                    reject(
                      new Error(`Vosk model load timed out for ${modelPath}`),
                    ),
                  MODEL_LOAD_TIMEOUT_MS,
                ),
              ),
            ]);
          let model = null;
          let lastErr = null;
          for (const modelPath of modelCandidates) {
            try {
              model = await loadModelWithTimeout(createModel, modelPath);
              if (model) break;
            } catch (e) {
              console.warn(
                "🎤 [CLIENT-STT] Model candidate failed:",
                modelPath,
                e?.message || e,
              );
              lastErr = e;
            }
          }
          if (!model) {
            throw (
              lastErr ||
              new Error("Vosk model load failed for all candidates")
            );
          }
          const recognizer = new model.KaldiRecognizer(16000);
          recognizer.setWords?.(true);
          recognizer.setPartialWords?.(false);
          let latestText = "";
          recognizer.on?.("result", (message) => {
            const t = message?.result?.text;
            if (typeof t === "string" && t.trim()) latestText = t.trim();
          });
          recognizer.on?.("partialresult", (message) => {
            const t = message?.result?.partial;
            if (typeof t === "string" && t.trim()) latestText = t.trim();
          });
          return async (pcmFloat32, options = {}) => {
            const sourceRate = Number(options?.inputSampleRate) || 16000;
            const normalized = downsampleTo16kMono(pcmFloat32, sourceRate);
            if (!normalized || normalized.length === 0) return { text: "" };
            const audioBuffer = createMonoAudioBuffer(normalized, 16000);
            if (typeof recognizer.acceptWaveform === "function") {
              recognizer.acceptWaveform(audioBuffer);
            } else {
              throw new Error("Vosk recognizer missing acceptWaveform API");
            }
            await new Promise((resolve) => setTimeout(resolve, 50));
            return { text: (latestText || "").trim() };
          };
        })();
        return __sttPromise;
      };
    }

    const warmup = async () => {
      try {
        await window.__notesaiClientSystemSttGet();
        window.__notesaiClientSystemSttReady = true;
        sessionStorage.setItem("client_system_stt_preloaded", "1");
        console.log("🎤 [CLIENT-STT] Preloaded system-audio model");
      } catch (e) {
        console.warn(
          "🎤 [CLIENT-STT] Preload skipped/failed:",
          e?.message || e,
        );
      }
    };

    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(() => {
        warmup();
      }, { timeout: 5000 });
      return () => {
        try {
          window.cancelIdleCallback(id);
        } catch (_) {}
      };
    }

    const t = setTimeout(() => {
      warmup();
    }, 2500);
    return () => clearTimeout(t);
  }, []);
  const [override, setOverride] = useState(false);
  const [refId, setRefId] = useState(undefined);

  // Role of the currently signed-in user — used by medstudent route guards.
  // null = not yet loaded, string = role fetched from users table.
  const [userRole, setUserRole] = useState(null);
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const supabase = await getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;
        // Query against the active (schema-aware, impersonation-aware) client so we
        // see the user's CURRENT role — not the stale public.users row. This is the
        // same pattern Navbar.js uses, and matches what ProtectedRoute decides on.
        const { data } = await supabase
          .from("users")
          .select("role")
          .eq("id", session.user.id)
          .single();
        if (data?.role) setUserRole(data.role);
      } catch (err) {
        console.warn("[App] fetchUserRole failed:", err.message);
      }
    };
    fetchUserRole();
  }, []);

  usePageTimeTracking();
  usePresencePinger();

  // Persist session state to localStorage
  useEffect(() => {
    localStorage.setItem("audioRecorderSession", JSON.stringify(session));
  }, [session]);

  // Global transcription modal state - persists across component remounts
  const [isTranscriptionModalOpen, setIsTranscriptionModalOpen] = useState(
    () => {
      // Clear any stale transcription modal state on page load
      const saved = sessionStorage.getItem("transcriptionModalOpen");
      if (saved === "true") {
        // Clear it to prevent modal from staying open after refresh
        sessionStorage.removeItem("transcriptionModalOpen");
        return false;
      }
      return false;
    },
  );

  // Transcription modal minimized state
  const [isTranscriptionModalMinimized, setIsTranscriptionModalMinimized] =
    useState(true);

  // Transcription modal size state for resizing
  const [transcriptionModalSize, setTranscriptionModalSize] = useState({
    width: 800,
    height: 600,
  });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);

  // Transcription modal position state
  const [transcriptionModalPosition, setTranscriptionModalPosition] = useState({
    x: window.innerWidth - 320,
    y: 150,
  });

  // Refs to store initial values when resize starts
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startWidthRef = useRef(0);
  const startHeightRef = useRef(0);

  // Global AI Assistant popup state - persists across navigation
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);
  const [aiAssistantData, setAIAssistantData] = useState({
    patientId: null,
    patientData: null,
    documentContent: null,
    initialMessages: null,
    initialSelectedSources: null,
    initialThreadId: null,
    initialThreads: null,
    useExtractedData: false,
    initialReportFile: null,
    initialReportType: null,
    initialReportFileNames: null,
    effectiveDate: null,
  });

  // Live Activity: report the global AI popup as an activity while it is open.
  useEffect(() => {
    if (isAIAssistantOpen) beginActivity("ai_chat", "global");
    else endActivity("ai_chat");
    return () => endActivity("ai_chat");
  }, [isAIAssistantOpen]);

  // Chat Bubble state - persists across navigation with patient context
  // A modal (e.g. fax assign) can ask for the bottom-right corner, where
  // the bubbles otherwise sit on top of its buttons.
  const floatingBubblesHidden = useFloatingBubblesHidden();
  const [chatBubblePatientId, setChatBubblePatientId] = useState(null);
  const [chatBubblePatientData, setChatBubblePatientData] = useState(null);
  // Always start with default right-side position when popup opens
  const getDefaultPosition = () => {
    const topBarHeight = 120;
    const defaultWidth = 500;
    const rightSideX = window.innerWidth - defaultWidth - 20;
    return { x: rightSideX, y: topBarHeight };
  };

  const [aiAssistantPosition, setAIAssistantPosition] =
    useState(getDefaultPosition());

  // Terms of Service modal state
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [hasCheckedTerms, setHasCheckedTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Epic schema selection/creation modal state
  // null | { type: 'none'|'multiple', ctx, epicData, schemas?: string[] }
  const [epicSchemaModal, setEpicSchemaModal] = useState(null);
  const [epicSchemaCreating, setEpicSchemaCreating] = useState(false);
  const [epicSeeding, setEpicSeeding] = useState(false);

  // NextGen schema selection/creation modal state
  // null | { type: 'none'|'multiple', ctx, nextgenData, schemas?: string[] }
  const [nextgenSchemaModal, setNextgenSchemaModal] = useState(null);
  const [nextgenSchemaCreating, setNextgenSchemaCreating] = useState(false);
  // Dev seed modal state
  const [epicSeedModal, setEpicSeedModal] = useState(false);
  const [epicSeedTabIndex, setEpicSeedTabIndex] = useState(0);
  const [epicSeedCustomType, setEpicSeedCustomType] = useState("report");
  const [epicSeedCustomTitle, setEpicSeedCustomTitle] = useState("");
  const [epicSeedCustomFormat, setEpicSeedCustomFormat] = useState("text");
  const [epicSeedCustomText, setEpicSeedCustomText] = useState("");
  const [epicSeedCustomFile, setEpicSeedCustomFile] = useState(null);

  // Reset position to default right-side when popup opens
  useEffect(() => {
    if (isAIAssistantOpen) {
      // Always reset to default position when opening
      setAIAssistantPosition(getDefaultPosition());
    }
  }, [isAIAssistantOpen]);

  // Save position to sessionStorage when it changes (but not on initial open)
  // Only save if popup has been open for a bit (user has had time to move it)
  const hasMovedRef = useRef(false);
  useEffect(() => {
    if (isAIAssistantOpen) {
      // Track if position has been moved from default
      const defaultPos = getDefaultPosition();
      if (
        Math.abs(aiAssistantPosition.x - defaultPos.x) > 10 ||
        Math.abs(aiAssistantPosition.y - defaultPos.y) > 10
      ) {
        hasMovedRef.current = true;
      }

      // Only save if user has moved it (not just the initial reset)
      if (hasMovedRef.current) {
        sessionStorage.setItem(
          "aiAssistantPosition",
          JSON.stringify(aiAssistantPosition),
        );
      }
    } else {
      // Reset flag when popup closes
      hasMovedRef.current = false;
    }
  }, [aiAssistantPosition, isAIAssistantOpen]);

  // When a transcript is saved, refresh AI assistant patient data so it sees the new transcript without page refresh
  useEffect(() => {
    const handler = async (e) => {
      const patientId = e.detail?.patientId;
      if (!patientId || !aiAssistantData.patientId || aiAssistantData.patientId !== patientId) return;
      try {
        const supabase = await getSupabase();
        const { data, error } = await supabase.from(tableName).select("*").eq("id", patientId).single();
        if (!error && data) setAIAssistantData((prev) => (prev.patientId === patientId ? { ...prev, patientData: [data] } : prev));
      } catch (_) {}
    };
    window.addEventListener("transcriptSaved", handler);
    return () => window.removeEventListener("transcriptSaved", handler);
  }, [aiAssistantData.patientId]);

  // Check for terms acceptance when on schedules page
  useEffect(() => {
    // Only check on schedules page
    if (location.pathname !== "/schedules") {
      return;
    }

    // Check if we've already shown the modal this session
    const hasShownThisSession = sessionStorage.getItem(
      "termsModalShownThisSession",
    );
    if (hasShownThisSession === "true") {
      setHasCheckedTerms(true);
      return;
    }

    let timer = null;

    const checkTermsAcceptance = async () => {
      try {
        // Use public client to check terms acceptance (global user setting)
        const {
          data: { session },
        } = await supabasePublic().auth.getSession();

        if (!session?.user) {
          setHasCheckedTerms(true);
          return;
        }

        // Check database for terms acceptance - use public schema
        const { data: userData, error } = await supabasePublic()
          .from("users")
          .select("terms_accepted")
          .eq("id", session.user.id)
          .single();

        if (error) {
          // If column doesn't exist (42703), don't show modal
          if (
            error.code === "42703" ||
            error.message?.includes("does not exist")
          ) {
            console.log(
              "terms_accepted column doesn't exist - modal will not be shown",
            );
            setHasCheckedTerms(true);
            return;
          }
          // For other errors, also don't show modal
          console.error("Error checking terms acceptance:", error);
          setHasCheckedTerms(true);
          return;
        }

        // Show modal if terms not accepted and user hasn't accepted in this session
        if (!userData?.terms_accepted && !isTermsModalOpen && !termsAccepted) {
          timer = setTimeout(() => {
            setIsTermsModalOpen(true);
            sessionStorage.setItem("termsModalShownThisSession", "true");
          }, 1500); // Delay to ensure page is loaded
        } else if (userData?.terms_accepted) {
          // Terms already accepted, mark as accepted to prevent re-checking
          setTermsAccepted(true);
        }

        setHasCheckedTerms(true);
      } catch (error) {
        console.error("Error in checkTermsAcceptance:", error);
        setHasCheckedTerms(true);
      }
    };

    // Check after a short delay to not block page load
    const checkTimer = setTimeout(() => {
      checkTermsAcceptance();
    }, 500);

    return () => {
      if (timer) clearTimeout(timer);
      if (checkTimer) clearTimeout(checkTimer);
    };
  }, [location.pathname, isTermsModalOpen, termsAccepted]);

  // Initialize credit tracking on app start
  useEffect(() => {
    const initApp = async () => {
      try {
        console.log("🚀 App: Initializing credit tracking");
        await initializeCreditTracking();
        console.log("🚀 App: Credit tracking initialized successfully");

        // Clear old localStorage password validation to ensure database-based access control
        clearOldLocalStorageAccess();
      } catch (error) {
        console.error("🚀 App: Error initializing credit tracking:", error);
      }
    };

    initApp();
  }, []);

  // Offer to switch schemas when a patient lands in a schema different from the user's current one
  const handleEpicSchemaMismatch = async (targetSchema) => {
    if (!targetSchema || targetSchema === supabaseSchema) return;
    try {
      const publicClient = supabasePublic();
      const { data: sessionData } = await publicClient.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) return;

      const { data: userData } = await publicClient
        .from("users")
        .select("schema")
        .eq("id", userId)
        .single();

      const userSchemas = (userData?.schema || "public")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const hasAccess = userSchemas.includes(targetSchema);

      if (hasAccess) {
        toast({
          duration: null,
          isClosable: true,
          position: "top",
          render: ({ onClose }) => (
            <Box
              bg="blue.600"
              color="white"
              p={4}
              borderRadius="md"
              boxShadow="lg"
              maxW="400px"
            >
              <Text fontWeight="bold" mb={1}>
                Patient added to {targetSchema}
              </Text>
              <Text fontSize="sm" mb={3}>
                You're currently viewing <strong>{supabaseSchema}</strong>.
                Switch to <strong>{targetSchema}</strong> to view this patient.
              </Text>
              <HStack spacing={2} justify="flex-end">
                <Button
                  size="sm"
                  variant="ghost"
                  color="white"
                  _hover={{ bg: "blue.500" }}
                  onClick={onClose}
                >
                  Dismiss
                </Button>
                <Button
                  size="sm"
                  bg="white"
                  color="blue.600"
                  _hover={{ bg: "blue.50" }}
                  onClick={async () => {
                    const reordered = [
                      targetSchema,
                      ...userSchemas.filter((s) => s !== targetSchema),
                    ].join(",");
                    await publicClient
                      .from("users")
                      .update({ schema: reordered })
                      .eq("id", userId);
                    onClose();
                    window.location.reload();
                  }}
                >
                  Switch to {targetSchema}
                </Button>
              </HStack>
            </Box>
          ),
        });
      } else {
        toast({
          title: `Patient added to ${targetSchema}`,
          description: `You don't have access to the ${targetSchema} schema. Contact your admin to gain access.`,
          status: "warning",
          duration: 10000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (err) {
      console.warn("Schema mismatch handling failed:", err);
    }
  };

  // Dismiss the Epic schema modal AND clear the backend session so page
  // refreshes don't re-trigger the full FHIR fetch loop.
  const dismissEpicModal = () => {
    setEpicSchemaModal(null);
    fetch("/api/epic/clear-session/", { method: "POST", credentials: "include" }).catch(() => {});
  };

  // Dismiss the NextGen schema modal AND clear the backend session
  const dismissNextGenModal = () => {
    setNextgenSchemaModal(null);
    fetch("/api/nextgen/clear-session/", { method: "POST", credentials: "include" }).catch(() => {});
  };

  // Create/navigate to a patient once a target schema is known
  const handleEpicProceed = async (ctx, epicData, targetSchema) => {
    if (ctx.matched_patient_uuid) {
      // Store Epic documents for matched patient (session still active here)
      let clinicalNotesText = "";
      try {
        const storeResp = await fetch("/api/epic/store-documents/", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            patient_uuid: ctx.matched_patient_uuid,
            schema_id: targetSchema,
            patient_name: ctx.epic_patient?.name || "",
            ehr_id: ctx.epic_patient?.patient_number || ctx.epic_patient?.fhir_id || "",
            encounter_date: ctx.encounter_date || "",
            documents: ctx.epic_documents || [],
          }),
        });
        if (storeResp.ok) {
          const stored = await storeResp.json();
          clinicalNotesText = stored.clinical_notes_text || "";
          console.log(
            `[Epic] Stored ${stored.stored_reports} report(s), ${stored.stored_forms} form(s) for matched patient`
          );
        }
      } catch (se) {
        console.warn("[Epic] store-documents failed (non-fatal):", se);
      }

      navigate(`/patient/${ctx.matched_patient_uuid}`, {
        state: { epicData: { ...epicData, clinicalNotesText } },
      });
      fetch("/api/epic/clear-session/", {
        method: "POST",
        credentials: "include",
      }).catch(() => {});
      await handleEpicSchemaMismatch(targetSchema);
      return;
    }

    if (!ctx.epic_patient?.name) return;

    try {
      const supabase = await getSupabase();
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;

      const createResp = await fetch("/api/epic/create-patient/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schema_id: targetSchema,
          user_id: userId,
          patient_name: ctx.epic_patient.name,
          dob: ctx.epic_patient.dob,
          ehr_id: ctx.epic_patient.patient_number || ctx.epic_patient.fhir_id,
          email: ctx.epic_patient.email || null,
          tel_num: ctx.epic_patient.phone || null,
          physician_name: ctx.physician_name || null,
          date_: ctx.encounter_date || "",
          time_: ctx.encounter_time || "",
          encounter_date: ctx.encounter_date || "",
          documents: ctx.epic_documents || [],
        }),
      });

      if (createResp.ok) {
        const created = await createResp.json();
        const clinicalNotesText = created.clinical_notes_text || "";
        console.log(
          `[Epic] New patient created. Stored ${created.stored_reports} report(s), ${created.stored_forms} form(s).`
        );
        navigate(`/patient/${created.patient_uuid}`, {
          state: { epicData: { ...epicData, clinicalNotesText } },
        });
        if (targetSchema !== supabaseSchema) {
          await handleEpicSchemaMismatch(targetSchema);
        } else {
          toast({
            title: "Patient added from Epic",
            description: `${ctx.epic_patient.name} has been added to your schedule.`,
            status: "success",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        }
      } else {
        toast({
          title: "Epic patient could not be added",
          description: `${ctx.epic_patient.name} was found in Epic but couldn't be saved to NotesAI. Please add manually.`,
          status: "warning",
          duration: 8000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (createErr) {
      console.warn("Epic patient creation failed:", createErr);
      toast({
        title: "Epic patient not found in NotesAI",
        description: `"${ctx.epic_patient?.name}" could not be matched or added to NotesAI.`,
        status: "warning",
        duration: 8000,
        isClosable: true,
        position: "top",
      });
    }
  };

  // Create/navigate to a patient once a NextGen target schema is known
  const handleNextGenProceed = async (ctx, nextgenData, targetSchema) => {
    if (ctx.matched_patient_uuid) {
      let clinicalNotesText = "";
      try {
        const storeResp = await fetch("/api/nextgen/store-documents/", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            patient_uuid: ctx.matched_patient_uuid,
            schema_id: targetSchema,
            patient_name: ctx.nextgen_patient?.name || "",
            ehr_id: ctx.nextgen_patient?.patient_number || ctx.nextgen_patient?.fhir_id || "",
            encounter_date: ctx.encounter_date || "",
            documents: ctx.nextgen_documents || [],
          }),
        });
        if (storeResp.ok) {
          const stored = await storeResp.json();
          clinicalNotesText = stored.clinical_notes_text || "";
          console.log(
            `[NextGen] Stored ${stored.stored_reports} report(s), ${stored.stored_forms} form(s) for matched patient`
          );
        }
      } catch (se) {
        console.warn("[NextGen] store-documents failed (non-fatal):", se);
      }

      navigate(`/patient/${ctx.matched_patient_uuid}`, {
        state: { nextgenData: { ...nextgenData, clinicalNotesText } },
      });
      fetch("/api/nextgen/clear-session/", {
        method: "POST",
        credentials: "include",
      }).catch(() => {});
      await handleEpicSchemaMismatch(targetSchema);
      return;
    }

    if (!ctx.nextgen_patient?.name) return;

    try {
      const supabase = await getSupabase();
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;

      const createResp = await fetch("/api/nextgen/create-patient/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schema_id: targetSchema,
          user_id: userId,
          patient_name: ctx.nextgen_patient.name,
          dob: ctx.nextgen_patient.dob,
          ehr_id: ctx.nextgen_patient.patient_number || ctx.nextgen_patient.fhir_id,
          email: ctx.nextgen_patient.email || null,
          tel_num: ctx.nextgen_patient.phone || null,
          physician_name: ctx.physician_name || null,
          date_: ctx.encounter_date || "",
          time_: ctx.encounter_time || "",
          encounter_date: ctx.encounter_date || "",
          documents: ctx.nextgen_documents || [],
        }),
      });

      if (createResp.ok) {
        const created = await createResp.json();
        const clinicalNotesText = created.clinical_notes_text || "";
        console.log(
          `[NextGen] New patient created. Stored ${created.stored_reports} report(s), ${created.stored_forms} form(s).`
        );
        navigate(`/patient/${created.patient_uuid}`, {
          state: { nextgenData: { ...nextgenData, clinicalNotesText } },
        });
        if (targetSchema !== supabaseSchema) {
          await handleEpicSchemaMismatch(targetSchema);
        } else {
          toast({
            title: "Patient added from NextGen",
            description: `${ctx.nextgen_patient.name} has been added to your schedule.`,
            status: "success",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        }
      } else {
        toast({
          title: "NextGen patient could not be added",
          description: `${ctx.nextgen_patient.name} was found in NextGen but couldn't be saved to NotesAI. Please add manually.`,
          status: "warning",
          duration: 8000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (createErr) {
      console.warn("NextGen patient creation failed:", createErr);
      toast({
        title: "NextGen patient not found in NotesAI",
        description: `"${ctx.nextgen_patient?.name}" could not be matched or added to NotesAI.`,
        status: "warning",
        duration: 8000,
        isClosable: true,
        position: "top",
      });
    }
  };

  // Check Epic SMART on FHIR session and navigate to matched patient on launch
  useEffect(() => {
    const checkEpicContext = async () => {
      try {
        const statusResp = await fetch("/api/epic/status/", {
          credentials: "include",
        });
        if (!statusResp.ok) return;
        const statusData = await statusResp.json();
        if (!statusData.connected) return;

        const ctxResp = await fetch("/api/epic/patient-context/", {
          credentials: "include",
        });
        if (!ctxResp.ok) return;
        const ctx = await ctxResp.json();

        const epicData = {
          epicPatient: ctx.epic_patient,
          epicDocuments: ctx.epic_documents,
          practitionerFhirId: ctx.practitioner_fhir_id,
        };

        // Matched patient — store Epic documents via handleEpicProceed
        if (ctx.matched_patient_uuid) {
          const physicianSchemas = ctx.physician_schemas || [];
          const targetSchema =
            physicianSchemas[0] || ctx.physician_schema || "public";
          await handleEpicProceed(ctx, epicData, targetSchema);
          return;
        }

        // Patient not in NotesAI yet — determine which schema to create them in
        if (ctx.epic_patient?.name) {
          const physicianSchemas = ctx.physician_schemas || [];

          if (physicianSchemas.length === 0) {
            // No schema found for this physician — prompt to create one
            setEpicSchemaModal({ type: "none", ctx, epicData });
            return;
          }

          if (physicianSchemas.length > 1) {
            // Multiple schemas — ask the doctor which one to use
            setEpicSchemaModal({ type: "multiple", ctx, epicData, schemas: physicianSchemas });
            return;
          }

          // Exactly one schema — proceed directly
          await handleEpicProceed(ctx, epicData, physicianSchemas[0]);
        }
      } catch (err) {
        console.warn("Epic context check failed:", err);
      }
    };

    checkEpicContext();
    // Run once on mount — Epic session is set server-side before the app loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check NextGen SMART on FHIR (EAL) session and navigate to matched patient on launch
  useEffect(() => {
    const checkNextGenContext = async () => {
      try {
        const statusResp = await fetch("/api/nextgen/status/", {
          credentials: "include",
        });
        if (!statusResp.ok) return;
        const statusData = await statusResp.json();
        if (!statusData.connected) return;

        const ctxResp = await fetch("/api/nextgen/patient-context/", {
          credentials: "include",
        });
        if (!ctxResp.ok) return;
        const ctx = await ctxResp.json();

        const nextgenData = {
          nextgenPatient: ctx.nextgen_patient,
          nextgenDocuments: ctx.nextgen_documents,
          practitionerFhirId: ctx.practitioner_fhir_id,
        };

        // Matched patient — store NextGen documents
        if (ctx.matched_patient_uuid) {
          const physicianSchemas = ctx.physician_schemas || [];
          const targetSchema =
            physicianSchemas[0] || ctx.physician_schema || "public";
          await handleNextGenProceed(ctx, nextgenData, targetSchema);
          return;
        }

        // Patient not in NotesAI yet — determine which schema to create them in
        if (ctx.nextgen_patient?.name) {
          const physicianSchemas = ctx.physician_schemas || [];

          if (physicianSchemas.length === 0) {
            setNextgenSchemaModal({ type: "none", ctx, nextgenData });
            return;
          }

          if (physicianSchemas.length > 1) {
            setNextgenSchemaModal({ type: "multiple", ctx, nextgenData, schemas: physicianSchemas });
            return;
          }

          // Exactly one schema — proceed directly
          await handleNextGenProceed(ctx, nextgenData, physicianSchemas[0]);
        }
      } catch (err) {
        // Common causes:
        //   • Supabase 522 — Cloudflare gateway timeout, DB momentarily unreachable
        //   • CORS block — Supabase project paused or URL misconfigured
        //   • HTML response instead of JSON — /api/nextgen/status/ returned a 404/error page
        // None of these affect the rest of the app — NextGen context is optional.
        if (err?.message?.includes('Failed to fetch') || err?.message?.includes('522') || err?.message?.includes('CORS')) {
          console.warn("[NextGen] Skipping NextGen context check — Supabase/backend unreachable (likely a 522 Cloudflare timeout or CORS error). This only affects NextGen EHR integration and does not impact the rest of the app.");
        } else if (err instanceof SyntaxError) {
          console.warn("[NextGen] Skipping NextGen context check — received HTML instead of JSON (backend returned an error page). Check that /api/nextgen/status/ is reachable.");
        } else {
          console.warn("[NextGen] NextGen context check failed:", err?.message || err);
        }
      }
    };

    checkNextGenContext();
    // Run once on mount — NextGen session is set server-side before the app loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear transcription modal state on page refresh/unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem("transcriptionModalOpen");
      sessionStorage.removeItem("transcriptionFacts");
      sessionStorage.removeItem("transcriptionTranscript");
      sessionStorage.removeItem("transcriptionSoapNotes");
      sessionStorage.removeItem("transcriptionAudioDuration");
      sessionStorage.removeItem("preservedPatientId");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Effect to manage mousemove and mouseup listeners for resizing
  useEffect(() => {
    console.log(
      "🔧 useEffect for resize listeners triggered. isResizing:",
      isResizing,
    );

    if (!isResizing) {
      return; // Don't set up listeners if not resizing
    }

    const handleGlobalMouseMove = (e) => {
      console.log(
        "🔧 Global mousemove triggered - isResizing:",
        isResizing,
        "isMinimized:",
        isTranscriptionModalMinimized,
      );

      // Double-check the current state
      if (isTranscriptionModalMinimized) {
        console.log("🔧 Resize blocked - modal is minimized");
        return;
      }

      const deltaX = e.clientX - startXRef.current;
      const deltaY = e.clientY - startYRef.current;

      let newWidth = startWidthRef.current;
      let newHeight = startHeightRef.current;

      if (resizeDirection === "se") {
        newWidth = Math.max(400, startWidthRef.current + deltaX);
        newHeight = Math.max(300, startHeightRef.current + deltaY);
      } else if (resizeDirection === "sw") {
        newWidth = Math.max(400, startWidthRef.current - deltaX);
        newHeight = Math.max(300, startHeightRef.current + deltaY);
      } else if (resizeDirection === "ne") {
        newWidth = Math.max(400, startWidthRef.current + deltaX);
        newHeight = Math.max(300, startHeightRef.current - deltaY);
      } else if (resizeDirection === "nw") {
        newWidth = Math.max(400, startWidthRef.current - deltaX);
        newHeight = Math.max(300, startHeightRef.current - deltaY);
      }

      console.log("🔧 Updating size to:", { newWidth, newHeight });
      setTranscriptionModalSize({ width: newWidth, height: newHeight });
    };

    const handleGlobalMouseUp = () => {
      console.log("🔧 Global mouseup triggered - stopping resize");
      setIsResizing(false);
      setResizeDirection(null);
    };

    console.log("🔧 Adding global event listeners");
    document.addEventListener("mousemove", handleGlobalMouseMove);
    document.addEventListener("mouseup", handleGlobalMouseUp);

    // Cleanup function
    return () => {
      console.log("🔧 Removing global event listeners");
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isResizing, isTranscriptionModalMinimized, resizeDirection]); // Dependencies for useEffect

  // Debug effect to track isResizing changes
  useEffect(() => {
    console.log("🔧 isResizing state changed to:", isResizing);
  }, [isResizing]);

  // Listen for minimize transcription modal event
  useEffect(() => {
    const handleMinimizeTranscriptionModal = () => {
      console.log("🔧 Minimizing transcription modal from recording start");
      setIsTranscriptionModalMinimized(true);
    };

    window.addEventListener(
      "minimizeTranscriptionModal",
      handleMinimizeTranscriptionModal,
    );

    return () => {
      window.removeEventListener(
        "minimizeTranscriptionModal",
        handleMinimizeTranscriptionModal,
      );
    };
  }, []);

  const [transcriptionData, setTranscriptionData] = useState({
    transcript: null,
    facts: null,
    soapNotes: null,
    audioDuration: 0,
    audioFileName: "",
    patientId: null,
    isProcessing: false,
    estimatedProgress: 0,
    currentMilestone: "",
    elapsedTime: 0,
    shouldShowResults: false,
  });

  // Comment handlers for transcription results
  const handleCommentChange = (value) => {
    setTranscriptionData((prev) => ({
      ...prev,
      newComment: value,
    }));
  };

  const handleAddComment = () => {
    if (
      !transcriptionData.newComment ||
      transcriptionData.newComment.trim() === ""
    ) {
      return;
    }

    const newComment = {
      text: transcriptionData.newComment.trim(),
      timestamp: new Date().toISOString(),
    };

    setTranscriptionData((prev) => ({
      ...prev,
      comments: [...(prev.comments || []), newComment],
      newComment: "",
    }));

    toast({
      title: "Success",
      description: "Comment added successfully.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleDeleteComment = (commentIndex) => {
    setTranscriptionData((prev) => ({
      ...prev,
      comments: prev.comments
        ? prev.comments.filter((_, index) => index !== commentIndex)
        : [],
    }));

    toast({
      title: "Success",
      description: "Comment deleted successfully.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // List the path where the persistent component should be excluded
  const excludedPath = "/visit/:uuid";

  // Function to handle modal resizing - only sets initial state
  const handleMouseDown = (e, direction) => {
    console.log("🔧 Resize handle clicked:", direction);
    console.log("🔧 Modal minimized:", isTranscriptionModalMinimized);
    console.log("🔧 Current size:", transcriptionModalSize);
    console.log("🔧 Current isResizing state:", isResizing);

    e.preventDefault();
    e.stopPropagation(); // Prevent Draggable from also starting to drag

    console.log("🔧 Setting isResizing to true...");
    setIsResizing(true);
    setResizeDirection(direction);

    // Store initial mouse position and modal size
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    startWidthRef.current = transcriptionModalSize.width;
    startHeightRef.current = transcriptionModalSize.height;

    console.log("🔧 Resize started - initial position:", {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: transcriptionModalSize.width,
      startHeight: transcriptionModalSize.height,
    });
  };

  // Function to save transcription results manually
  const saveTranscriptionResults = async () => {
    console.log("🔍 saveTranscriptionResults called with data:", {
      hasTranscript: !!transcriptionData.transcript,
      hasFacts: !!transcriptionData.facts,
      hasSoapNotes: !!transcriptionData.soapNotes,
      transcriptData: transcriptionData.transcript,
      factsData: transcriptionData.facts,
      soapNotesData: transcriptionData.soapNotes,
    });

    // Only require transcript and facts - SOAP notes are optional
    if (!transcriptionData.transcript || !transcriptionData.facts) {
      console.log(
        "❌ No transcription data to save - missing transcript or facts",
      );
      toast({
        title: "Cannot Save",
        description:
          "Transcription data is incomplete. Please transcribe the recording first.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const supabase = await getSupabase();

      // Get the current patient ID - prioritize URL over transcription data
      let patientId = null;

      // First, try to get patient ID from transcription data (most reliable, it's the actual UUID)
      if (transcriptionData.patientId) {
        patientId = transcriptionData.patientId;
        console.log(
          "✅ Found patient ID (UUID) from transcription data:",
          patientId,
        );
      }

      // If no transcription patient ID, try sessionStorage
      if (!patientId) {
        patientId = sessionStorage.getItem("preservedPatientId");
        console.log("✅ Found patient ID from sessionStorage:", patientId);
      }

      // Last resort: try URL, but validate it's a UUID format
      if (!patientId) {
        const pathParts = window.location.pathname.split("/");
        const visitIndex = pathParts.findIndex((part) => part === "visit");
        if (visitIndex !== -1 && pathParts[visitIndex + 1]) {
          const urlId = pathParts[visitIndex + 1];
          // Check if it's a UUID format (contains hyphens)
          if (urlId.includes("-")) {
            patientId = urlId;
            console.log(
              "✅ Found patient ID (UUID) from URL path (after 'visit'):",
              patientId,
            );
          } else {
            console.log(
              "⚠️ URL ID is not a UUID format (numeric):",
              urlId,
              "- will need to query by name or date",
            );
          }
        }
      }

      // Additional check: if we're in a visit page, try to get the patient ID from the URL params
      if (!patientId) {
        const urlParams = new URLSearchParams(window.location.search);
        const visitPatientId = urlParams.get("patientId");
        if (visitPatientId) {
          patientId = visitPatientId;
          console.log("✅ Found patient ID from URL params:", patientId);
        }
      }

      if (!patientId) {
        console.error("❌ Cannot save results without a patient record");
        toast({
          title: "Save Failed",
          description:
            "No patient record found. Please navigate to a patient page first.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // First, get existing transcriptions
      const { data: existingData, error: fetchError } = await supabase
        .from(tableName)
        .select("transcripts")
        .eq("id", patientId)
        .single();

      if (fetchError && fetchError.code !== "PGRST116") {
        // PGRST116 is "not found"
        throw fetchError;
      }

      // Log transcription data for debugging
      console.log("🔍 Manual save: Transcription data:", transcriptionData);
      console.log(
        "🔍 Manual save: audioDuration from transcriptionData:",
        transcriptionData.audioDuration,
      );

      // Try to get duration from sessionStorage if not available in transcription data
      let audioDuration = transcriptionData.audioDuration;
      if (!audioDuration || audioDuration <= 0) {
        const storedDuration = sessionStorage.getItem(
          "transcriptionAudioDuration",
        );
        if (storedDuration) {
          audioDuration = JSON.parse(storedDuration);
          console.log(
            "🔍 Manual save: Retrieved duration from sessionStorage:",
            audioDuration,
          );
        }
      }

      // Create new transcription data
      const newTranscription = {
        id: Date.now().toString(), // Generate unique ID
        patient_id: patientId?.toString() || "",
        name: "Manual Save", // Since we don't have userInfo here
        transcript: Array.isArray(transcriptionData.transcript)
          ? transcriptionData.transcript
          : [transcriptionData.transcript],
        facts: Array.isArray(transcriptionData.facts)
          ? transcriptionData.facts
          : [transcriptionData.facts],
        soap_notes: transcriptionData.soapNotes || {},
        audio_duration: audioDuration > 0 ? audioDuration : 0, // Use retrieved duration
        audio_source: transcriptionData.processingMethod || "live_recording", // Use processingMethod
        audio_file_name: transcriptionData.audioFileName || "Live Recording",
        estimated_credit_usage: transcriptionData.creditUsage || null, // Include credit usage from transcription data
        created_at: new Date().toISOString(),
      };

      console.log("🔍 Manual save: Final audio duration used:", audioDuration);
      console.log(
        "🔍 Manual save: New transcription object:",
        newTranscription,
      );

      // Prepare the updated transcripts array
      let updatedTranscripts = [];
      if (existingData && existingData.transcripts) {
        // If existing data is an array, use it; otherwise, convert single object to array
        if (Array.isArray(existingData.transcripts)) {
          updatedTranscripts = [...existingData.transcripts, newTranscription];
        } else {
          // Convert single transcription object to array and add new one
          updatedTranscripts = [existingData.transcripts, newTranscription];
        }
      } else {
        // No existing transcriptions, start with new one
        updatedTranscripts = [newTranscription];
      }

      // Log before update
      console.log("Manual save: Updating patient record with id:", patientId);
      console.log(
        "Manual save: Updated transcripts array:",
        updatedTranscripts,
      );
      console.log("Manual save: Current URL:", window.location.href);
      console.log("Manual save: URL pathname:", window.location.pathname);
      console.log("Manual save: URL search:", window.location.search);

      // Update the transcripts column with the array
      const { data, error } = await supabase
        .from(tableName)
        .update({ transcripts: updatedTranscripts })
        .eq("id", patientId)
        .select();

      // Log after update
      console.log("Manual save: Update result data:", data);
      if (error) throw error;

      console.log("✅ Manual save: Transcription results saved successfully");

      // Add recorded credits to the user's total
      if (transcriptionData.creditUsage) {
        console.log(
          "💳 Manual save: Adding recorded credits to user total:",
          transcriptionData.creditUsage,
        );
        addRecordedCredits(transcriptionData.creditUsage).catch((error) => {
          console.error(
            "💳 Manual save: Error adding recorded credits:",
            error,
          );
        });
      }

      // Show success alert
      toast({
        title: "Transcription Saved!",
        description: "Your transcription has been manually saved.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      // Dispatch event to notify PatientPage to refresh
      window.dispatchEvent(new CustomEvent("patientDataRefresh"));
      window.dispatchEvent(new CustomEvent("transcriptSaved", { detail: { patientId } }));
    } catch (error) {
      console.error(
        "❌ Manual save: Error saving transcription to Supabase:",
        error,
      );
      console.error("❌ Error details:", {
        message: error?.message,
        code: error?.code,
        details: error?.details,
        hint: error?.hint,
        fullError: error,
      });

      const errorMessage =
        error?.message ||
        error?.details ||
        "Failed to save transcription results.";

      toast({
        title: "Save Failed",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <ChakraProvider theme={theme}>
      <SingleTabSessionGuard />
      <BadNumberConfirmHost />
        {/* Global style to ensure toasts appear above modals */}
        <style>{`
        .chakra-toast__inner, 
        .chakra-toast,
        [id^="chakra-toast"],
        [class*="chakra-portal"] > div[style*="position: fixed"] {
          z-index: 99999 !important;
        }
      `}</style>
        {/* <> */}
        {/* {showPersistentComponent && location.pathname !== excludedPath && ( */}
        <PersistentComponent
          showPersistentComponent={showPersistentComponent}
          setShowPersistentComponent={setShowPersistentComponent}
          link={link}
          condFunc={condFunc}
        >
          <AudioRecorder
            state={stateData}
            refId={refId}
            session={session}
            setSession={setSession}
            override={override}
            setShowPersistentComponent={setShowPersistentComponent}
            condFunc={condFunc}
            isTranscriptionModalOpen={isTranscriptionModalOpen}
            setIsTranscriptionModalOpen={setIsTranscriptionModalOpen}
            transcriptionData={transcriptionData}
            setTranscriptionData={setTranscriptionData}
          />
        </PersistentComponent>
        {/* )}   */}

        {/* Global Transcription Modal - persists across component remounts */}
        {isTranscriptionModalOpen && (
          <Draggable
            handle=".drag-handle-transcription"
            bounds="body"
            position={transcriptionModalPosition}
            onStop={(e, data) => {
              setTranscriptionModalPosition({ x: data.x, y: data.y });
            }}
            onMouseDown={(e) => {
              // Only allow dragging from the header, not from content areas
              if (!e.target.closest(".drag-handle-transcription")) {
                e.stopPropagation();
              }
            }}
          >
            <Box
              position="fixed"
              w={
                isTranscriptionModalMinimized
                  ? "300px"
                  : `${transcriptionModalSize.width}px`
              }
              h={
                isTranscriptionModalMinimized
                  ? "120px"
                  : `${transcriptionModalSize.height}px`
              }
              bg="white"
              border="1px solid #ccc"
              borderRadius="md"
              boxShadow="lg"
              zIndex={9999}
              overflow="hidden"
              cursor={isResizing ? "nwse-resize" : "move"}
              transition={
                isTranscriptionModalMinimized ? "all 0.3s ease" : "none"
              }
              onWheel={(e) => {
                // Prevent wheel events from bubbling to the main page
                e.stopPropagation();
              }}
              onScroll={(e) => {
                // Prevent scroll events from bubbling to the main page
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                // Prevent touch move events from bubbling to the main page
                e.stopPropagation();
              }}
            >
              <Box
                className="drag-handle-transcription"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={4}
                borderBottom="1px solid #ccc"
                bg="gray.50"
                cursor="move"
              >
                <Text fontWeight="bold" fontSize="lg">
                  Transcription Results
                </Text>
                <HStack spacing={2}>
                  <IconButton
                    icon={
                      isTranscriptionModalMinimized ? (
                        <FiMaximize2 />
                      ) : (
                        <FiMinimize2 />
                      )
                    }
                    size="sm"
                    aria-label={
                      isTranscriptionModalMinimized
                        ? "Maximize transcription results"
                        : "Minimize transcription results"
                    }
                    onClick={() => {
                      console.log("🔧 Maximize/Minimize button clicked");
                      console.log(
                        "🔧 Current minimized state:",
                        isTranscriptionModalMinimized,
                      );
                      const newMinimizedState = !isTranscriptionModalMinimized;
                      console.log("🔧 New minimized state:", newMinimizedState);

                      setIsTranscriptionModalMinimized(newMinimizedState);
                      if (isTranscriptionModalMinimized) {
                        // When maximizing, set to default size
                        console.log("🔧 Maximizing - setting default size");
                        setTranscriptionModalSize({ width: 800, height: 600 });
                      } else {
                        // When minimizing, return to default position
                        console.log(
                          "🔧 Minimizing - returning to default position",
                        );
                        setTranscriptionModalPosition({
                          x: window.innerWidth - 320,
                          y: 150,
                        });
                      }
                    }}
                    variant="ghost"
                    _hover={{ bg: "gray.200" }}
                  />
                  <IconButton
                    icon={<FiX />}
                    size="sm"
                    aria-label="Close transcription results"
                    onClick={() => {
                      setIsTranscriptionModalOpen(false);
                      sessionStorage.setItem("transcriptionModalOpen", "false");
                      setTranscriptionData({
                        transcript: null,
                        facts: null,
                        soapNotes: null,
                        audioDuration: 0,
                        audioFileName: "",
                        patientId: null,
                        isProcessing: false,
                        estimatedProgress: 0,
                        currentMilestone: "",
                        elapsedTime: 0,
                        shouldShowResults: false,
                      });
                      // Clear from sessionStorage
                      sessionStorage.removeItem("transcriptionTranscript");
                      sessionStorage.removeItem("transcriptionFacts");
                      sessionStorage.removeItem("transcriptionSoapNotes");
                      sessionStorage.removeItem("transcriptionAudioDuration");
                      sessionStorage.removeItem("preservedPatientId");
                    }}
                  />
                </HStack>
              </Box>

              {/* Resize handles - only show when maximized */}
              {console.log(
                "🔧 Rendering resize handles - minimized:",
                isTranscriptionModalMinimized,
              )}
              {!isTranscriptionModalMinimized && (
                <>
                  {/* Bottom-right resize handle */}
                  <Box
                    position="absolute"
                    bottom="0"
                    right="0"
                    w="12px"
                    h="12px"
                    bg="blue.500"
                    cursor="se-resize"
                    onMouseDown={(e) => handleMouseDown(e, "se")}
                    zIndex={10000}
                  />
                  {/* Bottom-left resize handle */}
                  <Box
                    position="absolute"
                    bottom="0"
                    left="0"
                    w="12px"
                    h="12px"
                    bg="blue.500"
                    cursor="sw-resize"
                    onMouseDown={(e) => handleMouseDown(e, "sw")}
                    zIndex={10000}
                  />
                  {/* Top-right resize handle */}
                  <Box
                    position="absolute"
                    top="0"
                    right="0"
                    w="12px"
                    h="12px"
                    bg="blue.500"
                    cursor="ne-resize"
                    onMouseDown={(e) => handleMouseDown(e, "ne")}
                    zIndex={10000}
                  />
                  {/* Top-left resize handle */}
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    w="12px"
                    h="12px"
                    bg="blue.500"
                    cursor="nw-resize"
                    onMouseDown={(e) => handleMouseDown(e, "nw")}
                    zIndex={10000}
                  />
                </>
              )}

              {isTranscriptionModalMinimized ? (
                // Minimized view - show summary and action buttons
                <Box p={4}>
                  <VStack spacing={3} align="stretch">
                    {/* Status indicator */}
                    {transcriptionData.isProcessing ? (
                      <HStack spacing={2}>
                        <Spinner size="sm" color="blue.500" />
                        <Text fontSize="sm" color="blue.600">
                          Processing transcription...
                        </Text>
                      </HStack>
                    ) : transcriptionData.transcript ? (
                      <HStack spacing={2}>
                        <Box w={2} h={2} bg="green.500" borderRadius="full" />
                        <Text fontSize="sm" color="green.600">
                          Transcription complete
                        </Text>
                      </HStack>
                    ) : (
                      <HStack spacing={2}>
                        <Box w={2} h={2} bg="gray.400" borderRadius="full" />
                        <Text fontSize="sm" color="gray.600">
                          Ready
                        </Text>
                      </HStack>
                    )}

                    {/* Quick stats */}
                    {transcriptionData.transcript && (
                      <VStack spacing={1} align="stretch">
                        <Text fontSize="xs" color="gray.500">
                          {Array.isArray(transcriptionData.transcript)
                            ? transcriptionData.transcript.length
                            : 1}{" "}
                          transcript lines •
                          {Array.isArray(transcriptionData.facts)
                            ? transcriptionData.facts.length
                            : 0}{" "}
                          facts extracted
                        </Text>
                        {transcriptionData.creditUsage && (
                          <Text fontSize="xs" color="blue.500">
                            💳 Credits: {transcriptionData.creditUsage}
                          </Text>
                        )}
                      </VStack>
                    )}

                    {/* Action buttons */}
                    {transcriptionData.transcript &&
                      transcriptionData.facts && (
                        <HStack spacing={2} justify="center">
                          {/* Show Save button if auto-save is disabled */}
                          {(() => {
                            // Get the current patient ID to check auto-save setting
                            let patientId = null;
                            const pathParts =
                              window.location.pathname.split("/");
                            const visitIndex = pathParts.findIndex(
                              (part) => part === "visit",
                            );
                            if (
                              visitIndex !== -1 &&
                              pathParts[visitIndex + 1]
                            ) {
                              patientId = pathParts[visitIndex + 1];
                            } else {
                              patientId =
                                sessionStorage.getItem("preservedPatientId");
                            }

                            // Check if auto-save is disabled for this patient
                            const autoSaveSetting = patientId
                              ? getAutoSaveSetting(patientId)
                              : null;
                            const isAutoSaveDisabled =
                              autoSaveSetting === false;

                            return isAutoSaveDisabled ? (
                              <Button
                                size="sm"
                                colorScheme="green"
                                onClick={saveTranscriptionResults}
                              >
                                Save
                              </Button>
                            ) : null;
                          })()}
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={() => {
                              // Generate PDF for transcription results
                              const doc = new jsPDF();

                              // Add title
                              doc.setFontSize(20);
                              doc.text("Medical Report Transcription", 20, 20);

                              // Add patient info
                              doc.setFontSize(14);
                              doc.text(
                                `Patient: ${
                                  transcriptionData.audioFileName || "Unknown"
                                }`,
                                20,
                                35,
                              );
                              doc.text(
                                `Date: ${new Date().toLocaleDateString()}`,
                                20,
                                45,
                              );
                              doc.text(
                                `Duration: ${
                                  transcriptionData.audioDuration || 0
                                } seconds`,
                                20,
                                55,
                              );

                              // Add transcript
                              doc.setFontSize(16);
                              doc.text("Transcript:", 20, 75);
                              doc.setFontSize(10);
                              let yPos = 85;
                              transcriptionData.transcript?.forEach(
                                (line, index) => {
                                  if (yPos > 280) {
                                    doc.addPage();
                                    yPos = 20;
                                  }
                                  // Split long lines to fit page width
                                  const lines = doc.splitTextToSize(line, 170);
                                  lines.forEach((splitLine) => {
                                    if (yPos > 280) {
                                      doc.addPage();
                                      yPos = 20;
                                    }
                                    doc.text(splitLine, 20, yPos);
                                    yPos += 6;
                                  });
                                },
                              );

                              // Add facts
                              doc.addPage();
                              doc.setFontSize(16);
                              doc.text("Extracted Facts:", 20, 20);
                              doc.setFontSize(10);
                              yPos = 30;
                              transcriptionData.facts?.forEach(
                                (fact, index) => {
                                  if (yPos > 280) {
                                    doc.addPage();
                                    yPos = 20;
                                  }
                                  const factText = `${index + 1}. ${
                                    fact.text
                                  } (Category: ${fact.group})`;
                                  const lines = doc.splitTextToSize(
                                    factText,
                                    170,
                                  );
                                  lines.forEach((line) => {
                                    if (yPos > 280) {
                                      doc.addPage();
                                      yPos = 20;
                                    }
                                    doc.text(line, 20, yPos);
                                    yPos += 6;
                                  });
                                  yPos += 3; // Add space between facts
                                },
                              );

                              // Add SOAP Notes
                              if (
                                transcriptionData.soapNotes &&
                                (transcriptionData.soapNotes.transcript_only ||
                                  transcriptionData.soapNotes.facts_only)
                              ) {
                                doc.addPage();
                                doc.setFontSize(16);
                                doc.text("SOAP Notes:", 20, 20);
                                doc.setFontSize(10);
                                yPos = 30;

                                if (
                                  transcriptionData.soapNotes.transcript_only
                                ) {
                                  doc.setFontSize(12);
                                  doc.text(
                                    "Transcript-Based SOAP Notes:",
                                    20,
                                    yPos,
                                  );
                                  yPos += 10;
                                  doc.setFontSize(10);
                                  const soapLines = doc.splitTextToSize(
                                    transcriptionData.soapNotes.transcript_only,
                                    170,
                                  );
                                  soapLines.forEach((line) => {
                                    if (yPos > 280) {
                                      doc.addPage();
                                      yPos = 20;
                                    }
                                    doc.text(line, 20, yPos);
                                    yPos += 6;
                                  });
                                  yPos += 10;
                                }

                                if (transcriptionData.soapNotes.facts_only) {
                                  if (yPos > 250) {
                                    doc.addPage();
                                    yPos = 20;
                                  }
                                  doc.setFontSize(12);
                                  doc.text("Facts-Based SOAP Notes:", 20, yPos);
                                  yPos += 10;
                                  doc.setFontSize(10);
                                  const soapLines = doc.splitTextToSize(
                                    transcriptionData.soapNotes.facts_only,
                                    170,
                                  );
                                  soapLines.forEach((line) => {
                                    if (yPos > 280) {
                                      doc.addPage();
                                      yPos = 20;
                                    }
                                    doc.text(line, 20, yPos);
                                    yPos += 6;
                                  });
                                }
                              }

                              // Save the PDF
                              const timestamp = new Date().getTime();
                              doc.save(
                                `transcription_${
                                  transcriptionData.audioFileName || "report"
                                }_${timestamp}.pdf`,
                              );
                            }}
                          >
                            Export to PDF
                          </Button>
                        </HStack>
                      )}
                  </VStack>
                </Box>
              ) : (
                // Maximized view - show full content
                <Box
                  maxH="calc(90vh - 80px)"
                  overflowY="auto"
                  p={4}
                  onWheel={(e) => {
                    e.stopPropagation();
                    // Remove preventDefault to fix passive event listener error
                  }}
                  onScroll={(e) => {
                    e.stopPropagation();
                    // Remove preventDefault to fix passive event listener error
                  }}
                  onTouchMove={(e) => {
                    e.stopPropagation();
                    // Remove preventDefault to fix passive event listener error
                  }}
                  style={{
                    overscrollBehavior: "contain",
                    scrollBehavior: "smooth",
                  }}
                >
                  <VStack spacing={4} align="stretch">
                    {/* Action buttons at the top */}
                    {transcriptionData.transcript &&
                      transcriptionData.facts && (
                        <HStack spacing={3} mb={4} justify="flex-end">
                          {/* Show Save button if auto-save is disabled */}
                          {(() => {
                            // Get the current patient ID to check auto-save setting
                            let patientId = null;
                            const pathParts =
                              window.location.pathname.split("/");
                            const visitIndex = pathParts.findIndex(
                              (part) => part === "visit",
                            );
                            if (
                              visitIndex !== -1 &&
                              pathParts[visitIndex + 1]
                            ) {
                              patientId = pathParts[visitIndex + 1];
                            } else {
                              patientId =
                                sessionStorage.getItem("preservedPatientId");
                            }

                            // Check if auto-save is disabled for this patient
                            const autoSaveSetting = patientId
                              ? getAutoSaveSetting(patientId)
                              : null;
                            const isAutoSaveDisabled =
                              autoSaveSetting === false;

                            return isAutoSaveDisabled ? (
                              <Button
                                colorScheme="green"
                                onClick={saveTranscriptionResults}
                              >
                                Save
                              </Button>
                            ) : null;
                          })()}
                          <Button
                            colorScheme="blue"
                            onClick={() => {
                              // Generate PDF for transcription results
                              const doc = new jsPDF();

                              // Add title
                              doc.setFontSize(20);
                              doc.text("Medical Report Transcription", 20, 20);

                              // Add patient info
                              doc.setFontSize(14);
                              doc.text(
                                `Patient: ${
                                  transcriptionData.audioFileName || "Unknown"
                                }`,
                                20,
                                35,
                              );
                              doc.text(
                                `Date: ${new Date().toLocaleDateString()}`,
                                20,
                                45,
                              );
                              doc.text(
                                `Duration: ${
                                  transcriptionData.audioDuration || 0
                                } seconds`,
                                20,
                                55,
                              );

                              // Add transcript
                              doc.setFontSize(16);
                              doc.text("Transcript:", 20, 75);
                              doc.setFontSize(10);
                              let yPos = 85;
                              transcriptionData.transcript?.forEach(
                                (line, index) => {
                                  if (yPos > 280) {
                                    doc.addPage();
                                    yPos = 20;
                                  }
                                  // Split long lines to fit page width
                                  const lines = doc.splitTextToSize(line, 170);
                                  lines.forEach((splitLine) => {
                                    if (yPos > 280) {
                                      doc.addPage();
                                      yPos = 20;
                                    }
                                    doc.text(splitLine, 20, yPos);
                                    yPos += 6;
                                  });
                                },
                              );

                              // Add facts
                              doc.addPage();
                              doc.setFontSize(16);
                              doc.text("Extracted Facts:", 20, 20);
                              doc.setFontSize(10);
                              yPos = 30;
                              transcriptionData.facts?.forEach(
                                (fact, index) => {
                                  if (yPos > 280) {
                                    doc.addPage();
                                    yPos = 20;
                                  }
                                  const factText = `${index + 1}. ${
                                    fact.text
                                  } (Category: ${fact.group})`;
                                  const lines = doc.splitTextToSize(
                                    factText,
                                    170,
                                  );
                                  lines.forEach((line) => {
                                    if (yPos > 280) {
                                      doc.addPage();
                                      yPos = 20;
                                    }
                                    doc.text(line, 20, yPos);
                                    yPos += 6;
                                  });
                                  yPos += 3; // Add space between facts
                                },
                              );

                              // Add SOAP Notes
                              if (
                                transcriptionData.soapNotes &&
                                (transcriptionData.soapNotes.transcript_only ||
                                  transcriptionData.soapNotes.facts_only)
                              ) {
                                doc.addPage();
                                doc.setFontSize(16);
                                doc.text("SOAP Notes:", 20, 20);
                                doc.setFontSize(10);
                                yPos = 30;

                                if (
                                  transcriptionData.soapNotes.transcript_only
                                ) {
                                  doc.setFontSize(12);
                                  doc.text(
                                    "Transcript-Based SOAP Notes:",
                                    20,
                                    yPos,
                                  );
                                  yPos += 10;
                                  doc.setFontSize(10);
                                  const soapLines = doc.splitTextToSize(
                                    transcriptionData.soapNotes.transcript_only,
                                    170,
                                  );
                                  soapLines.forEach((line) => {
                                    if (yPos > 280) {
                                      doc.addPage();
                                      yPos = 20;
                                    }
                                    doc.text(line, 20, yPos);
                                    yPos += 6;
                                  });
                                  yPos += 10;
                                }

                                if (transcriptionData.soapNotes.facts_only) {
                                  if (yPos > 250) {
                                    doc.addPage();
                                    yPos = 20;
                                  }
                                  doc.setFontSize(12);
                                  doc.text("Facts-Based SOAP Notes:", 20, yPos);
                                  yPos += 10;
                                  doc.setFontSize(10);
                                  const soapLines = doc.splitTextToSize(
                                    transcriptionData.soapNotes.facts_only,
                                    170,
                                  );
                                  soapLines.forEach((line) => {
                                    if (yPos > 280) {
                                      doc.addPage();
                                      yPos = 20;
                                    }
                                    doc.text(line, 20, yPos);
                                    yPos += 6;
                                  });
                                }
                              }

                              // Save the PDF
                              const timestamp = new Date().getTime();
                              doc.save(
                                `transcription_${
                                  transcriptionData.audioFileName || "report"
                                }_${timestamp}.pdf`,
                              );
                            }}
                          >
                            Export to PDF
                          </Button>
                        </HStack>
                      )}

                    {/* Transcription Metadata */}
                    <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                      <VStack spacing={2} align="stretch">
                        <HStack spacing={4} fontSize="sm" color="gray.600">
                          <Text>
                            🎵 Audio Duration:{" "}
                            {transcriptionData.audioDuration
                              ? `${Math.floor(
                                  transcriptionData.audioDuration / 60,
                                )}:${(transcriptionData.audioDuration % 60)
                                  .toString()
                                  .padStart(2, "0")}`
                              : "Duration unknown"}
                          </Text>
                          <Text>
                            🎵 Audio:{" "}
                            {transcriptionData.audioFileName || "Unknown"}
                          </Text>
                          <Text>
                            📝 Source:{" "}
                            {transcriptionData.processingMethod === "rest_api"
                              ? "Patient Audio"
                              : transcriptionData.processingMethod ===
                                  "live_recording"
                                ? "Live Recording"
                                : "Uploaded File"}
                          </Text>
                          {transcriptionData.creditUsage && (
                            <Text>
                              💳 Credits Used: {transcriptionData.creditUsage}
                            </Text>
                          )}
                        </HStack>
                      </VStack>
                    </Box>

                    {console.log("🔍 Transcription display check:", {
                      hasTranscript: !!transcriptionData.transcript,
                      shouldShowResults: transcriptionData.shouldShowResults,
                      transcript: transcriptionData.transcript,
                    })}
                    {transcriptionData.transcript &&
                      transcriptionData.shouldShowResults && (
                        <Box>
                          <Heading size="md" mb={2}>
                            Transcript
                          </Heading>
                          <Box
                            maxH="300px"
                            overflowY="auto"
                            p={4}
                            borderWidth={1}
                            borderRadius="md"
                            onWheel={(e) => {
                              e.stopPropagation();
                              // Remove preventDefault to fix passive event listener error
                            }}
                            onScroll={(e) => {
                              e.stopPropagation();
                              // Remove preventDefault to fix passive event listener error
                            }}
                            onTouchMove={(e) => {
                              e.stopPropagation();
                              // Remove preventDefault to fix passive event listener error
                            }}
                            style={{
                              overscrollBehavior: "contain",
                              scrollBehavior: "smooth",
                            }}
                          >
                            {Array.isArray(transcriptionData.transcript) ? (
                              transcriptionData.transcript.map(
                                (line, index) => (
                                  <Text key={index} mb={2}>
                                    {line}
                                  </Text>
                                ),
                              )
                            ) : (
                              <Text>{transcriptionData.transcript}</Text>
                            )}
                          </Box>
                        </Box>
                      )}

                    {transcriptionData.facts &&
                      transcriptionData.shouldShowResults && (
                        <Box>
                          <Heading size="md" mb={2}>
                            Extracted Facts
                          </Heading>
                          <Box
                            maxH="300px"
                            overflowY="auto"
                            p={4}
                            borderWidth={1}
                            borderRadius="md"
                            onWheel={(e) => {
                              e.stopPropagation();
                              // Remove preventDefault to fix passive event listener error
                            }}
                            onScroll={(e) => {
                              e.stopPropagation();
                              // Remove preventDefault to fix passive event listener error
                            }}
                            onTouchMove={(e) => {
                              e.stopPropagation();
                              // Remove preventDefault to fix passive event listener error
                            }}
                            style={{
                              overscrollBehavior: "contain",
                              scrollBehavior: "smooth",
                            }}
                          >
                            {Array.isArray(transcriptionData.facts) ? (
                              transcriptionData.facts.map((fact, index) => (
                                <Box
                                  key={index}
                                  mb={2}
                                  p={2}
                                  bg="gray.50"
                                  borderRadius="md"
                                >
                                  <Text>{fact.text}</Text>
                                  <Text fontSize="sm" color="gray.500">
                                    Category: {fact.group}
                                  </Text>
                                </Box>
                              ))
                            ) : (
                              <Text>{transcriptionData.facts}</Text>
                            )}
                          </Box>
                        </Box>
                      )}

                    {transcriptionData.soapNotes &&
                      (transcriptionData.soapNotes.transcript_only ||
                        transcriptionData.soapNotes.facts_only) &&
                      transcriptionData.shouldShowResults && (
                        <Box>
                          {transcriptionData.soapNotes.transcript_only && (
                            <Box mb={4}>
                              <Heading size="md" mb={2}>
                                SOAP Notes (Transcript-Based)
                              </Heading>
                              <Box
                                maxH="300px"
                                overflowY="auto"
                                p={4}
                                borderWidth={1}
                                borderRadius="md"
                                onWheel={(e) => {
                                  e.stopPropagation();
                                  // Remove preventDefault to fix passive event listener error
                                }}
                                onScroll={(e) => {
                                  e.stopPropagation();
                                  // Remove preventDefault to fix passive event listener error
                                }}
                                onTouchMove={(e) => {
                                  e.stopPropagation();
                                  // Remove preventDefault to fix passive event listener error
                                }}
                                style={{
                                  overscrollBehavior: "contain",
                                  scrollBehavior: "smooth",
                                }}
                              >
                                <Text whiteSpace="pre-wrap" fontSize="sm">
                                  {transcriptionData.soapNotes.transcript_only}
                                </Text>
                              </Box>
                            </Box>
                          )}

                          {transcriptionData.soapNotes.facts_only && (
                            <Box>
                              <Heading size="md" mb={2}>
                                SOAP Notes (Facts-Based)
                              </Heading>
                              <Box
                                maxH="300px"
                                overflowY="auto"
                                p={4}
                                borderWidth={1}
                                borderRadius="md"
                                onWheel={(e) => {
                                  e.stopPropagation();
                                  // Remove preventDefault to fix passive event listener error
                                }}
                                onScroll={(e) => {
                                  e.stopPropagation();
                                  // Remove preventDefault to fix passive event listener error
                                }}
                                onTouchMove={(e) => {
                                  e.stopPropagation();
                                  // Remove preventDefault to fix passive event listener error
                                }}
                                style={{
                                  overscrollBehavior: "contain",
                                  scrollBehavior: "smooth",
                                }}
                              >
                                <Text whiteSpace="pre-wrap" fontSize="sm">
                                  {transcriptionData.soapNotes.facts_only}
                                </Text>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}

                    {/* Comments Section */}
                    <Box>
                      <Text fontWeight="semibold" mb={2} color="orange.600">
                        💬 Comments:
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {/* Display existing comments */}
                        {transcriptionData.comments &&
                          transcriptionData.comments.length > 0 && (
                            <Box
                              maxH="150px"
                              overflowY="auto"
                              p={3}
                              bg="orange.50"
                              borderRadius="md"
                              borderWidth={1}
                            >
                              {transcriptionData.comments.map(
                                (comment, commentIndex) => (
                                  <Box
                                    key={commentIndex}
                                    mb={2}
                                    p={2}
                                    bg="white"
                                    borderRadius="md"
                                    borderWidth={1}
                                  >
                                    <HStack justify="space-between" mb={1}>
                                      <Text fontSize="xs" color="gray.500">
                                        {new Date(
                                          comment.timestamp,
                                        ).toLocaleString()}
                                      </Text>
                                      <Button
                                        size="xs"
                                        colorScheme="red"
                                        variant="ghost"
                                        onClick={() =>
                                          handleDeleteComment(commentIndex)
                                        }
                                      >
                                        ✕
                                      </Button>
                                    </HStack>
                                    <Text fontSize="sm">{comment.text}</Text>
                                  </Box>
                                ),
                              )}
                            </Box>
                          )}

                        {/* Add new comment */}
                        <HStack spacing={2}>
                          <Input
                            placeholder="Add a comment..."
                            value={transcriptionData.newComment || ""}
                            onChange={(e) =>
                              handleCommentChange(e.target.value)
                            }
                            size="sm"
                            flex={1}
                          />
                          <Button
                            size="sm"
                            colorScheme="orange"
                            onClick={handleAddComment}
                            isDisabled={
                              !transcriptionData.newComment ||
                              transcriptionData.newComment.trim() === ""
                            }
                          >
                            Add
                          </Button>
                        </HStack>
                      </VStack>
                    </Box>

                    {/* Processing indicator with progress */}
                    {transcriptionData.isProcessing && (
                      <Box textAlign="center">
                        <VStack spacing={4}>
                          <Spinner size="lg" color="blue.500" />
                          <Text fontWeight="bold">
                            Processing transcription...
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {transcriptionData.currentMilestone ||
                              "Initializing..."}
                          </Text>

                          {/* Progress bar */}
                          <Box w="100%" maxW="400px">
                            <Progress
                              value={transcriptionData.estimatedProgress}
                              size="lg"
                              colorScheme="blue"
                              borderRadius="md"
                              mb={2}
                            />
                            <Text fontSize="sm" color="gray.500">
                              {transcriptionData.estimatedProgress}% complete •{" "}
                              {transcriptionData.elapsedTime}s elapsed
                            </Text>
                          </Box>

                          <Text fontSize="xs" color="gray.500">
                            Estimated time based on your recording duration
                          </Text>
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </Box>
              )}
            </Box>
          </Draggable>
        )}

        {/* Global AI Assistant Popup - persists across navigation */}
        {isAIAssistantOpen && (
          <AIAssistant
            patientId={aiAssistantData.patientId}
            patientData={aiAssistantData.patientData}
            documentContent={aiAssistantData.documentContent}
            isMinimized={false}
            isGlobalPopup={true}
            onClose={() => setIsAIAssistantOpen(false)}
            globalPosition={aiAssistantPosition}
            onPositionChange={setAIAssistantPosition}
            initialMessages={aiAssistantData.initialMessages}
            initialSelectedSources={aiAssistantData.initialSelectedSources}
            initialThreadId={aiAssistantData.initialThreadId}
            initialThreads={aiAssistantData.initialThreads}
            useExtractedData={aiAssistantData.useExtractedData}
            initialReportFile={aiAssistantData.initialReportFile}
            initialReportType={aiAssistantData.initialReportType}
            initialReportFileNames={aiAssistantData.initialReportFileNames}
            effectiveDate={aiAssistantData.effectiveDate}
          />
        )}

        {/* Persistent Chat Bubble Widget - auto-syncs patient context.
            Hidden on every route that renders the clinic chart so it doesn't
            overlap the medstudent layout (that page has its own mic + AI
            panel), and hidden for read-only patients (Reports / Record / Chat
            bubbles are clinician tools).

            This used to enumerate /clinic/ and /patients/:uuid only, missing
            /visit/:uuid and /patient/:uuid — which render the SAME page. That
            mounted a second RecordingBubble there, and since it has no patient
            (chatBubblePatientId is never set) it answered the broadcast
            `recordingBubbleOpen` with a "No patient selected" toast while the
            chart's own bubble opened the recorder correctly. */}
        {/* ...and hidden while a modal has asked for the corner (the fax
            assign modal does): they sit above it and cover its buttons. */}
        {!floatingBubblesHidden &&
          !isClinicChartPath(location.pathname) &&
          !isPatientRole(userRole) && (
          <BubbleContainer
            currentPatientId={chatBubblePatientId}
            currentPatientData={chatBubblePatientData}
          />
        )}

        {/* Global host for the Reports bubble's pop-out modal */}
        <GlobalReportsModalHost />

        {/* Global Help Desk modal (opened by HelpDeskButton and external recorder popup) */}
        <HelpDeskModal />

        <GlobalConfirmationHandler />
        <NavBar />
        <MessagingHost />
        <ImpersonationBanner />
        <Routes>
          <Route exact path="/" element={<LandingPage />} />
          <Route path="/auth" element={<AuthUI />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/complete-signup" element={<CompleteSignup />} />
          <Route path="/auth/confirm" element={<EmailConfirmation />} />
          <Route path="/auth/patient-verification" element={<PatientVerification />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/403" element={<Forbidden />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/help-desk" element={<HelpDeskPage />} />
          {/* Personal booking link — public so external guests can book */}
          <Route path="/book/:slug" element={<BookMeetingPage />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/audio-recorder" element={<AudioRecorderPage />} />
            <Route path="/recording" element={<RecordingPage />} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/recent-audios" element={<RecentAudios />} />
            <Route path="/schedules/*" element={<Schedules />} />
            <Route path="/patients" element={<PatientListPage />} />
            <Route
              path="/patients/:uuid"
              element={
                <MedStudentClinicPage
                  source="roster"
                  setStateData={setStateData}
                  setRefId={setRefId}
                  setShowPersistentComponent={setShowPersistentComponent}
                />
              }
            />
            <Route path="/no-shows" element={<NoShows />} />
            <Route path="/upload-json" element={<JsonUploader />} />
            <Route path="/file-upload" element={<FileUploader />} />
            <Route path="/upload-files/:id" element={<FileUploader />} />
            <Route path="/pdf-viewer" element={<PdfViewer />} />
            <Route path="/signout" element={<SignOut />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/agents" element={<AgentsPage />} />
            <Route path="/confirm-admin" element={<ConfirmAdmin />} />
            <Route path="/admin-page" element={<AdminPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/admin-dashboard" element={<AdminDashboard />} />
            <Route path="/super-admin" element={<SuperAdmin />} />
            <Route path="/monitor" element={<MonitorPage />} />
            <Route
              path="/visit/:uuid"
              element={
                <MedStudentClinicPage
                  setStateData={setStateData}
                  setRefId={setRefId}
                  setShowPersistentComponent={setShowPersistentComponent}
                />
              }
            />
            <Route
              path="/clinic/:uuid"
              element={
                <MedStudentClinicPage
                  setStateData={setStateData}
                  setRefId={setRefId}
                  setShowPersistentComponent={setShowPersistentComponent}
                />
              }
            />
            <Route
              path="/patient/:uuid"
              element={
                <MedStudentClinicPage
                  setStateData={setStateData}
                  setRefId={setRefId}
                  setShowPersistentComponent={setShowPersistentComponent}
                />
              }
            />
            <Route
              path="/patient/:uuid/edit-document"
              element={
                <EditableDocumentPage
                  setShowPersistentComponent={setShowPersistentComponent}
                  setStateData={setStateData}
                  setSession={setSession}
                  setLink={setLink}
                  setCondFunc={setCondFunc}
                  setOverride={setOverride}
                  setRefId={setRefId}
                  onOpenAIAssistant={(
                    patientId,
                    patientData,
                    documentContent,
                    initialMessages = null,
                    initialSelectedSources = null,
                    initialThreadId = null,
                    initialThreads = null,
                    useExtractedData = false,
                    initialReportFile = null,
                    initialReportType = null,
                    initialReportFileNames = null,
                    effectiveDate = null,
                  ) => {
                    setAIAssistantData({
                      patientId,
                      patientData,
                      documentContent,
                      initialMessages,
                      initialSelectedSources,
                      initialThreadId,
                      initialThreads,
                      useExtractedData,
                      initialReportFile,
                      initialReportType,
                      initialReportFileNames,
                      effectiveDate,
                    });
                    setIsAIAssistantOpen(true);
                  }}
                  isAIAssistantOpen={isAIAssistantOpen}
                  aiAssistantData={aiAssistantData}
                  onCloseAIAssistant={() => setIsAIAssistantOpen(false)}
                />
              }
            />
            <Route
              path="/visit/:uuid/edit-document/:documentId?"
              element={
                <EditableDocumentPage
                  setShowPersistentComponent={setShowPersistentComponent}
                  setStateData={setStateData}
                  setSession={setSession}
                  setLink={setLink}
                  setCondFunc={setCondFunc}
                  setOverride={setOverride}
                  setRefId={setRefId}
                  onOpenAIAssistant={(
                    patientId,
                    patientData,
                    documentContent,
                    initialMessages = null,
                    initialSelectedSources = null,
                    initialThreadId = null,
                    initialThreads = null,
                    useExtractedData = false,
                    initialReportFile = null,
                    initialReportType = null,
                    initialReportFileNames = null,
                    effectiveDate = null,
                  ) => {
                    setAIAssistantData({
                      patientId,
                      patientData,
                      documentContent,
                      initialMessages,
                      initialSelectedSources,
                      initialThreadId,
                      initialThreads,
                      useExtractedData,
                      initialReportFile,
                      initialReportType,
                      initialReportFileNames,
                      effectiveDate,
                    });
                    setIsAIAssistantOpen(true);
                  }}
                  isAIAssistantOpen={isAIAssistantOpen}
                  aiAssistantData={aiAssistantData}
                  onCloseAIAssistant={() => setIsAIAssistantOpen(false)}
                />
              }
            />
            <Route
              path="/edit-document"
              element={
                <EditableDocumentPage
                  setShowPersistentComponent={setShowPersistentComponent}
                  setStateData={setStateData}
                  setSession={setSession}
                  setLink={setLink}
                  setCondFunc={setCondFunc}
                  setOverride={setOverride}
                  setRefId={setRefId}
                  onOpenAIAssistant={(
                    patientId,
                    patientData,
                    documentContent,
                    initialMessages = null,
                    initialSelectedSources = null,
                    initialThreadId = null,
                    initialThreads = null,
                    useExtractedData = false,
                    initialReportFile = null,
                    initialReportType = null,
                    initialReportFileNames = null,
                    effectiveDate = null,
                  ) => {
                    setAIAssistantData({
                      patientId,
                      patientData,
                      documentContent,
                      initialMessages,
                      initialSelectedSources,
                      initialThreadId,
                      initialThreads,
                      useExtractedData,
                      initialReportFile,
                      initialReportType,
                      initialReportFileNames,
                      effectiveDate,
                    });
                    setIsAIAssistantOpen(true);
                  }}
                  isAIAssistantOpen={isAIAssistantOpen}
                  aiAssistantData={aiAssistantData}
                  onCloseAIAssistant={() => setIsAIAssistantOpen(false)}
                />
              }
            />
            <Route path="/analyze" element={<AnalyzePage />} />
          </Route>
        </Routes>

        {/* Terms of Service Modal - shows on schedules page after sign in if terms not accepted */}
        <TermsOfServiceModal
          isOpen={isTermsModalOpen}
          onClose={() => {
            setIsTermsModalOpen(false);
          }}
          onAccept={async () => {
            console.log("Terms of Service accepted");
            // Mark as accepted to prevent modal from reappearing
            setTermsAccepted(true);
            sessionStorage.removeItem("termsModalShownThisSession");
            // Verify the update was successful after a short delay
            try {
              await new Promise((resolve) => setTimeout(resolve, 500));
              const {
                data: { session },
              } = await supabasePublic().auth.getSession();
              if (session?.user) {
                const { data: userData } = await supabasePublic()
                  .from("users")
                  .select("terms_accepted")
                  .eq("id", session.user.id)
                  .single();
                console.log(
                  "Terms acceptance verified:",
                  userData?.terms_accepted,
                );
                if (userData?.terms_accepted) {
                  setTermsAccepted(true);
                }
              }
            } catch (error) {
              console.error("Error verifying terms acceptance:", error);
            }
          }}
        />
        {/* Epic schema selection / creation modal */}
        <Modal
          isOpen={epicSchemaModal !== null}
          onClose={dismissEpicModal}
          isCentered
          size="md"
        >
          <ModalOverlay />
          <ModalContent>
            {epicSchemaModal?.type === "none" && (
              <>
                <ModalHeader>No Schema Found</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Text mb={2}>
                    No schema was found matching your physician name{" "}
                    <strong>"{epicSchemaModal.ctx.physician_name}"</strong>.
                    Would you like to create a new schema?
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={4}>
                    If you already have a schema, please contact your admin if
                    you believe this is a mistake.
                  </Text>
                </ModalBody>
                <ModalFooter justifyContent="space-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    colorScheme="orange"
                    onClick={() => setEpicSeedModal(true)}
                    title="DEV: seed test documents onto the current Epic patient"
                  >
                    Seed Test Data (Dev)
                  </Button>
                  <HStack>
                    <Button
                      variant="ghost"
                      onClick={dismissEpicModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      colorScheme="blue"
                      isLoading={epicSchemaCreating}
                      onClick={async () => {
                        setEpicSchemaCreating(true);
                        try {
                          const supabase = await getSupabase();
                          const { data: sessionData } = await supabase.auth.getSession();
                          const userId = sessionData?.session?.user?.id;
                          const resp = await fetch("/api/epic/create-schema/", {
                            method: "POST",
                            credentials: "include",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              physician_name:
                                epicSchemaModal.ctx.physician_name,
                              user_id: userId,
                              current_schema: supabaseSchema,
                            }),
                          });
                          if (resp.ok) {
                            const result = await resp.json();
                            const { ctx, epicData } = epicSchemaModal;
                            setEpicSchemaModal(null);
                            await handleEpicProceed(ctx, epicData, result.schema_name);
                          } else {
                            toast({
                              title: "Schema creation failed",
                              description:
                                "Could not create a new schema. Please contact your admin.",
                              status: "error",
                              duration: 6000,
                              isClosable: true,
                            });
                          }
                        } catch (e) {
                          console.warn("Schema creation failed:", e);
                        } finally {
                          setEpicSchemaCreating(false);
                        }
                      }}
                    >
                      Create New Schema
                    </Button>
                  </HStack>
                </ModalFooter>
              </>
            )}

            {epicSchemaModal?.type === "multiple" && (
              <>
                <ModalHeader>Select Schema</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Text mb={4}>
                    Multiple schemas are associated with your physician name.
                    Which schema would you like to open this patient into?
                  </Text>
                  <VStack spacing={2} align="stretch">
                    {epicSchemaModal.schemas.map((schema) => (
                      <Button
                        key={schema}
                        variant="outline"
                        justifyContent="flex-start"
                        onClick={async () => {
                          const { ctx, epicData } = epicSchemaModal;
                          setEpicSchemaModal(null);
                          await handleEpicProceed(ctx, epicData, schema);
                        }}
                      >
                        {schema}
                      </Button>
                    ))}
                  </VStack>
                </ModalBody>
                <ModalFooter justifyContent="space-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    colorScheme="orange"
                    onClick={() => setEpicSeedModal(true)}
                    title="DEV: seed test documents onto the current Epic patient"
                  >
                    Seed Test Data (Dev)
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={dismissEpicModal}
                  >
                    Cancel
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>

        {/* NextGen schema selection / creation modal */}
        <Modal
          isOpen={nextgenSchemaModal !== null}
          onClose={dismissNextGenModal}
          isCentered
          size="md"
        >
          <ModalOverlay />
          <ModalContent>
            {nextgenSchemaModal?.type === "none" && (
              <>
                <ModalHeader>No Schema Found</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Text mb={2}>
                    No schema was found matching your physician name{" "}
                    <strong>"{nextgenSchemaModal.ctx.physician_name}"</strong>.
                    Would you like to create a new schema?
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={4}>
                    If you already have a schema, please contact your admin if
                    you believe this is a mistake.
                  </Text>
                </ModalBody>
                <ModalFooter>
                  <HStack>
                    <Button variant="ghost" onClick={dismissNextGenModal}>
                      Cancel
                    </Button>
                    <Button
                      colorScheme="teal"
                      isLoading={nextgenSchemaCreating}
                      onClick={async () => {
                        setNextgenSchemaCreating(true);
                        try {
                          const supabase = await getSupabase();
                          const { data: sessionData } = await supabase.auth.getSession();
                          const userId = sessionData?.session?.user?.id;
                          const resp = await fetch("/api/nextgen/create-schema/", {
                            method: "POST",
                            credentials: "include",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              physician_name: nextgenSchemaModal.ctx.physician_name,
                              user_id: userId,
                              current_schema: supabaseSchema,
                            }),
                          });
                          if (resp.ok) {
                            const result = await resp.json();
                            const { ctx, nextgenData } = nextgenSchemaModal;
                            setNextgenSchemaModal(null);
                            await handleNextGenProceed(ctx, nextgenData, result.schema_name);
                          } else {
                            toast({
                              title: "Schema creation failed",
                              description:
                                "Could not create a new schema. Please contact your admin.",
                              status: "error",
                              duration: 6000,
                              isClosable: true,
                            });
                          }
                        } catch (e) {
                          console.warn("NextGen schema creation failed:", e);
                        } finally {
                          setNextgenSchemaCreating(false);
                        }
                      }}
                    >
                      Create New Schema
                    </Button>
                  </HStack>
                </ModalFooter>
              </>
            )}

            {nextgenSchemaModal?.type === "multiple" && (
              <>
                <ModalHeader>Select Schema</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Text mb={4}>
                    Multiple schemas are associated with your physician name.
                    Which schema would you like to open this patient into?
                  </Text>
                  <VStack spacing={2} align="stretch">
                    {nextgenSchemaModal.schemas.map((schema) => (
                      <Button
                        key={schema}
                        variant="outline"
                        justifyContent="flex-start"
                        onClick={async () => {
                          const { ctx, nextgenData } = nextgenSchemaModal;
                          setNextgenSchemaModal(null);
                          await handleNextGenProceed(ctx, nextgenData, schema);
                        }}
                      >
                        {schema}
                      </Button>
                    ))}
                  </VStack>
                </ModalBody>
                <ModalFooter>
                  <Button variant="ghost" onClick={dismissNextGenModal}>
                    Cancel
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>

        {/* Epic dev seed modal */}
        <Modal
          isOpen={epicSeedModal}
          onClose={() => {
            setEpicSeedModal(false);
            setEpicSeedTabIndex(0);
            setEpicSeedCustomType("report");
            setEpicSeedCustomTitle("");
            setEpicSeedCustomFormat("text");
            setEpicSeedCustomText("");
            setEpicSeedCustomFile(null);
          }}
          isCentered
          size="lg"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontSize="md">Epic Test Data — Seed (Dev)</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={2}>
              <Tabs
                index={epicSeedTabIndex}
                onChange={(i) => setEpicSeedTabIndex(i)}
                variant="enclosed"
                size="sm"
              >
                <TabList>
                  <Tab>Preset Data</Tab>
                  <Tab>Custom Upload</Tab>
                </TabList>
                <TabPanels>
                  {/* ── Preset tab ─────────────────────────────────────── */}
                  <TabPanel px={0} pt={3}>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                      Seeds 14 test resources covering all 4 categories. Re-launch
                      from Epic afterward to test the ingestion pipeline.
                    </Text>
                    <VStack align="start" spacing={0} fontSize="xs" color="gray.700">
                      <Text fontWeight="semibold" mb={1}>Clinical Notes → injected into med-a doc:</Text>
                      <Text>• Progress Note, H&P Note, Discharge Summary, Consult Note</Text>
                      <Text fontWeight="semibold" mt={2} mb={1}>Transcript → uploaded to storage:</Text>
                      <Text>• Visit Transcript</Text>
                      <Text fontWeight="semibold" mt={2} mb={1}>Reports → uploaded to storage (7):</Text>
                      <Text>• Radiology, Pathology, ECG, CT Imaging, Lab, Sleep Study, PFT</Text>
                      <Text fontWeight="semibold" mt={2} mb={1}>Forms → Forms modal <Text as="span" color="purple.500">(direct DB insert)</Text>:</Text>
                      <Text>• Sleep Quality Survey</Text>
                      <Text>• Dyspnea Assessment</Text>
                    </VStack>
                  </TabPanel>

                  {/* ── Custom Upload tab ───────────────────────────────── */}
                  <TabPanel px={0} pt={3}>
                    <Text fontSize="sm" color="gray.600" mb={4}>
                      Seed a single document of your own onto the current Epic patient.
                    </Text>
                    <VStack spacing={3}>
                      <FormControl>
                        <FormLabel fontSize="sm" mb={1}>Document type</FormLabel>
                        <Select
                          size="sm"
                          value={epicSeedCustomType}
                          onChange={(e) => setEpicSeedCustomType(e.target.value)}
                        >
                          <option value="report">Report</option>
                          <option value="clinical_note">Clinical Note</option>
                          <option value="transcript">Transcript</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm" mb={1}>Title</FormLabel>
                        <Input
                          size="sm"
                          value={epicSeedCustomTitle}
                          onChange={(e) => setEpicSeedCustomTitle(e.target.value)}
                          placeholder="e.g. MRI Brain Report"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm" mb={1}>Format</FormLabel>
                        <Select
                          size="sm"
                          value={epicSeedCustomFormat}
                          onChange={(e) => {
                            setEpicSeedCustomFormat(e.target.value);
                            setEpicSeedCustomFile(null);
                            setEpicSeedCustomText("");
                          }}
                        >
                          <option value="text">Plain Text</option>
                          <option value="pdf">PDF File</option>
                        </Select>
                      </FormControl>
                      {epicSeedCustomFormat === "text" ? (
                        <FormControl>
                          <FormLabel fontSize="sm" mb={1}>Content</FormLabel>
                          <Textarea
                            size="sm"
                            rows={6}
                            value={epicSeedCustomText}
                            onChange={(e) => setEpicSeedCustomText(e.target.value)}
                            placeholder="Paste your document text here..."
                            fontFamily="mono"
                            fontSize="xs"
                          />
                        </FormControl>
                      ) : (
                        <FormControl>
                          <FormLabel fontSize="sm" mb={1}>PDF File</FormLabel>
                          <Input
                            size="sm"
                            type="file"
                            accept=".pdf,application/pdf"
                            onChange={(e) => setEpicSeedCustomFile(e.target.files[0] || null)}
                          />
                          {epicSeedCustomFile && (
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              Selected: {epicSeedCustomFile.name} ({Math.round(epicSeedCustomFile.size / 1024)} KB)
                            </Text>
                          )}
                        </FormControl>
                      )}
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={() => setEpicSeedModal(false)}
              >
                Cancel
              </Button>
              {epicSeedTabIndex === 0 ? (
                <Button
                  colorScheme="orange"
                  size="sm"
                  isLoading={epicSeeding}
                  onClick={async () => {
                    setEpicSeeding(true);
                    try {
                      const resp = await fetch("/api/epic/seed-test-patient/", {
                        method: "POST",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({}),
                      });
                      const result = await resp.json();
                      if (resp.ok) {
                        toast({
                          title: "Test data seeded",
                          description: `Created ${result.created?.length || 0} resource(s). Re-launch from Epic to test ingestion.`,
                          status: "success",
                          duration: 8000,
                          isClosable: true,
                        });
                        if (result.errors?.length) {
                          console.warn("[Epic Seed] Partial errors:", result.errors);
                          toast({
                            title: `${result.errors.length} resource(s) failed`,
                            description: result.errors.slice(0, 2).join("; "),
                            status: "warning",
                            duration: 6000,
                            isClosable: true,
                          });
                        }
                      } else {
                        toast({
                          title: "Seed failed",
                          description: result.error || "Unknown error",
                          status: "error",
                          duration: 6000,
                          isClosable: true,
                        });
                      }
                    } catch (e) {
                      console.warn("[Epic Seed] Failed:", e);
                      toast({ title: "Seed failed", status: "error", duration: 4000, isClosable: true });
                    } finally {
                      setEpicSeeding(false);
                    }
                  }}
                >
                  Seed All (9 documents)
                </Button>
              ) : (
                <Button
                  colorScheme="orange"
                  size="sm"
                  isLoading={epicSeeding}
                  isDisabled={
                    !epicSeedCustomTitle ||
                    (epicSeedCustomFormat === "text" && !epicSeedCustomText) ||
                    (epicSeedCustomFormat === "pdf" && !epicSeedCustomFile)
                  }
                  onClick={async () => {
                    setEpicSeeding(true);
                    try {
                      let resp;
                      if (epicSeedCustomFormat === "pdf" && epicSeedCustomFile) {
                        const fd = new FormData();
                        fd.append("document_type", epicSeedCustomType);
                        fd.append("title", epicSeedCustomTitle);
                        fd.append("file", epicSeedCustomFile);
                        resp = await fetch("/api/epic/seed-custom-document/", {
                          method: "POST",
                          credentials: "include",
                          body: fd,
                        });
                      } else {
                        resp = await fetch("/api/epic/seed-custom-document/", {
                          method: "POST",
                          credentials: "include",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            document_type: epicSeedCustomType,
                            title: epicSeedCustomTitle,
                            text: epicSeedCustomText,
                          }),
                        });
                      }
                      const result = await resp.json();
                      if (resp.ok) {
                        toast({
                          title: "Document seeded",
                          description: `"${result.created?.label}" created (ID: ${result.created?.id}). Re-launch from Epic to test ingestion.`,
                          status: "success",
                          duration: 8000,
                          isClosable: true,
                        });
                      } else {
                        toast({
                          title: "Seed failed",
                          description: result.error || "Unknown error",
                          status: "error",
                          duration: 6000,
                          isClosable: true,
                        });
                      }
                    } catch (e) {
                      console.warn("[Epic Custom Seed] Failed:", e);
                      toast({ title: "Seed failed", status: "error", duration: 4000, isClosable: true });
                    } finally {
                      setEpicSeeding(false);
                    }
                  }}
                >
                  Seed Document
                </Button>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* </> */}
    </ChakraProvider>
  );
}

export default App;
